"use client";
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as e from 'react';
import e__default, { forwardRef, createElement, useState, useRef, useEffect, useCallback, createContext, useContext } from 'react';
import { Slottable, Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import Gt, { createPortal } from 'react-dom';
import { Trigger, Icon, Portal, Content, Viewport, Item, ItemText, Root, Value } from '@radix-ui/react-select';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { Item as Item$1, Header, Trigger as Trigger$1, Content as Content$1, Root as Root$1 } from '@radix-ui/react-accordion';
import useEmblaCarousel from 'embla-carousel-react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Overlay as Overlay$1, Portal as Portal$1, Content as Content$2, Title, Root as Root$2, Close, Description } from '@radix-ui/react-dialog';
import { Root as Root$3, Trigger as Trigger$2, Content as Content$3, Arrow } from '@radix-ui/react-popover';

process.env.APIGEE_BASE_URL || "https://api-test.retailsso.com";
process.env.NEXT_PUBLIC_CDN_BASE_URL || "https://cdnprod.mafretailproxy.com";
process.env.ADTECH__K8S_BASE_URL ?? "";
process.env.BASKET__K8S_BASE_URL ?? "";
process.env.HOME_BFF__K8S_BASE_URL ?? "";
process.env.ORDER_BFF__K8S_BASE_URL ?? "";
process.env.PDP_BFF__K8S_BASE_URL ?? "";
process.env.SEARCH__K8S_BASE_URL ?? "";

const isServer = () => typeof window === "undefined";
const isClient = () => typeof window !== "undefined";
process.env.NODE_ENV === "development";

function getSanitizedValue(input) {
  return input.replace(/[<>]/g, "");
}

function cn$1(...inputs) {
  return twMerge(clsx(inputs));
}

const buttonVariants = cva(
  "focus-visible:ring-ring inline-flex items-center justify-center whitespace-nowrap rounded-xl font-bold transition-colors focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-[0.12]",
  {
    variants: {
      variant: {
        primary: "bg-primary text-white hover:bg-primary-active",
        secondary: "bg-secondary text-primary hover:bg-secondary-active",
        outline: "border-sm text-primary hover:bg-transparent",
        ghost: "text-primary",
        link: "text-primary underline-offset-4 hover:underline"
      },
      cSize: {
        xl: "p-md shadow-[0_2px_3px_1px_rgba(0,0,0,0.14)]",
        lg: "p-sm shadow-[0_2px_3px_1px_rgba(0,0,0,0.14)]",
        md: "p-xs shadow-[0_2px_3px_1px_rgba(0,0,0,0.14)]",
        sm: "p-xs shadow-[0_2px_3px_1px_rgba(0,0,0,0.14)]",
        xs: "p-2xs shadow-[0_2px_3px_1px_rgba(0,0,0,0.14)]",
        "2xs": "p-2xs shadow-[0_2px_3px_1px_rgba(0,0,0,0.14)]"
      },
      size: {
        xl: "px-3xl py-md text-xl",
        lg: "px-xl py-[14px] text-md",
        md: "px-lg py-xs text-md",
        sm: "px-md py-xs text-sm",
        xs: "px-sm py-2xs text-sm",
        "2xs": "px-xs py-2xs text-xs"
      },
      shape: {
        "soft-square": "rounded-sm",
        "semi-square": "rounded-lg",
        square: "rounded-xl",
        circle: "rounded-full border-sm",
        pill: "rounded-full border-sm p-0 py-[2px]"
      }
    },
    defaultVariants: {
      variant: "primary"
    }
  }
);
const Button = e.forwardRef(
  ({
    className,
    variant,
    size,
    asChild = false,
    Icon,
    iconPlacement,
    isIconOnly,
    shape,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot : "button";
    let iconClassLeft = "";
    let iconClassRight = "";
    switch (size) {
      case "xl":
        iconClassLeft = "pr-lg";
        iconClassRight = "pl-lg";
        break;
      case "lg":
        iconClassLeft = "pr-md";
        iconClassRight = "pl-md";
        break;
      case "md":
        iconClassLeft = "pr-sm";
        iconClassRight = "pl-sm";
        break;
      case "sm":
        iconClassLeft = "pr-xs";
        iconClassRight = "pl-xs";
        break;
      case "xs":
        iconClassLeft = "pr-xs";
        iconClassRight = "pl-xs";
        break;
      case "2xs":
        iconClassLeft = "pr-2xs";
        iconClassRight = "pl-2xs";
        break;
    }
    if (isIconOnly && Icon && shape) {
      return /* @__PURE__ */ jsx(
        Comp,
        {
          className: cn$1(
            buttonVariants({ variant, cSize: size, shape, className })
          ),
          ref,
          ...props,
          children: Icon
        }
      );
    }
    return /* @__PURE__ */ jsxs(
      Comp,
      {
        className: cn$1(buttonVariants({ variant, size, shape, className })),
        ref,
        ...props,
        children: [
          Icon && iconPlacement === "left" ? /* @__PURE__ */ jsx("div", { className: iconClassLeft, children: Icon }) : null,
          /* @__PURE__ */ jsx(Slottable, { children: props.children }),
          Icon && iconPlacement === "right" ? /* @__PURE__ */ jsx("div", { className: iconClassRight, children: Icon }) : null
        ]
      }
    );
  }
);
Button.displayName = "Button";

const badgeVariants = cva("inline-flex items-center justify-center font-bold", {
  variants: {
    variant: {
      outlined: "border-sm"
    },
    borderColor: {
      blue: "border-c4blue-50 text-primary",
      grey: "border-gray-100 text-gray-800"
    },
    mode: {
      light: "",
      dark: ""
    },
    color: {
      c4red: "",
      red: "",
      sharePrimary: "",
      yellow: "",
      green: "",
      purple: "",
      main: "",
      orange: "",
      blue: "",
      transparent: "",
      grey: "",
      sharemaroon: "",
      "c4blue-25": "",
      "c4red-500": "",
      "orange-500": "",
      "minsk-500": "",
      "persia-400": "",
      "blue-500": "",
      "blue-600": "",
      "polo-500": "",
      "gray-25": "",
      "green-500": "",
      "green-600": "",
      "gray-500": "",
      "c4blue-500": "",
      "orange-15": "",
      "red-400": "",
      "white-15": "",
      "white-25": "",
      "white-50": "",
      "white-100": ""
    },
    shape: {
      square: "rounded-sm",
      pill: "rounded-xl",
      "top-left-rounded": "rounded-bl-xs rounded-br-xl rounded-tl-xl rounded-tr-xs",
      "bottom-left-rounded": "rounded-bl-xl rounded-br-xs rounded-tl-xs rounded-tr-xl",
      roundedFull: "rounded-full"
    },
    size: {
      xl: "px-3xl py-md text-xl",
      lg: "px-xl py-[14px] text-lg",
      md: "px-lg py-xs text-md",
      sm: "px-md py-xs text-sm",
      xs: "px-sm py-2xs text-xs",
      "2xs": "p-2xs text-[10px]",
      tiny: "px-xs py-2xs text-[10px]",
      mini: "p-3xs text-[10px]"
    }
  },
  compoundVariants: [
    {
      mode: "light",
      color: "c4red",
      className: "bg-c4red-25 text-c4red-500"
    },
    {
      mode: "light",
      color: "c4blue-500",
      className: "bg-c4blue-500 text-white"
    },
    {
      mode: "light",
      color: "gray-25",
      className: "bg-gray-25 text-gray-400"
    },
    {
      mode: "light",
      color: "orange-500",
      className: "bg-orange-500 text-white"
    },
    {
      mode: "dark",
      color: "c4red",
      className: "bg-c4red-500 text-white"
    },
    {
      mode: "light",
      color: "orange-500",
      className: "bg-orange-500 text-white"
    },
    {
      mode: "light",
      color: "red",
      className: "bg-red-25 text-red-500"
    },
    {
      mode: "dark",
      color: "red",
      className: "bg-red-500 text-white"
    },
    {
      mode: "light",
      color: "persia-400",
      className: "bg-persia-400 text-white"
    },
    {
      mode: "light",
      color: "green-500",
      className: "bg-green-500 text-white"
    },
    {
      mode: "light",
      color: "blue-600",
      className: "bg-blue-600 text-white"
    },
    {
      mode: "light",
      color: "polo-500",
      className: "bg-polo-500 text-white"
    },
    {
      mode: "light",
      color: "blue-500",
      className: "bg-blue-500 text-white"
    },
    {
      mode: "light",
      color: "c4red-500",
      className: "bg-c4red-500 text-white"
    },
    {
      mode: "dark",
      color: "red-400",
      className: "bg-red-400 text-white"
    },
    {
      mode: "light",
      color: "minsk-500",
      className: "bg-minsk-500 text-white"
    },
    {
      mode: "light",
      color: "sharePrimary",
      className: "bg-shareprimary text-white"
    },
    {
      mode: "dark",
      color: "sharePrimary",
      className: "bg-shareprimary text-white"
    },
    {
      mode: "light",
      color: "yellow",
      className: "bg-gold-25 text-yellow-500"
    },
    {
      mode: "dark",
      color: "yellow",
      className: "bg-yellow-500 text-white"
    },
    {
      mode: "light",
      color: "green",
      className: "bg-green-25 text-green-600"
    },
    {
      mode: "dark",
      color: "green",
      className: "bg-persia-400 text-white"
    },
    {
      mode: "dark",
      color: "green-600",
      className: "bg-green-600 text-white"
    },
    {
      mode: "light",
      color: "purple",
      className: "bg-purple-25 text-purple-500"
    },
    {
      mode: "dark",
      color: "purple",
      className: "bg-purple-500 text-white"
    },
    {
      mode: "light",
      color: "main",
      className: "bg-secondary text-primary"
    },
    {
      mode: "dark",
      color: "main",
      className: "bg-primary text-white"
    },
    {
      mode: "light",
      color: "orange",
      className: "bg-orange-25 text-orange-500"
    },
    {
      mode: "dark",
      color: "orange",
      className: "bg-orange-500 text-white"
    },
    {
      mode: "light",
      color: "blue",
      className: "bg-blue-50 text-blue-600"
    },
    {
      mode: "dark",
      color: "blue",
      className: "bg-blue-400 text-white"
    },
    {
      mode: "light",
      color: "transparent",
      className: "bg-transparent"
    },
    {
      mode: "light",
      color: "c4blue-25",
      className: "bg-c4blue-25 text-primary"
    },
    {
      mode: "dark",
      color: "c4blue-25",
      className: "bg-primary text-c4blue-25"
    },
    {
      mode: "dark",
      color: "gray-500",
      className: "bg-gray-500 text-white"
    },
    {
      mode: "light",
      color: "orange-15",
      className: "bg-orange-15 text-sharedeepblue"
    },
    {
      mode: "light",
      color: "white-15",
      className: "bg-sharedeepblue text-white"
    },
    {
      mode: "light",
      color: "white-25",
      className: "bg-white text-persia-400"
    },
    {
      mode: "light",
      color: "white-50",
      className: "bg-white text-shareprimary"
    },
    {
      mode: "dark",
      color: "sharemaroon",
      className: "bg-sharemaroon text-white"
    },
    {
      mode: "light",
      color: "white-100",
      className: "bg-white text-sharemaroon"
    }
  ],
  defaultVariants: {
    mode: "light",
    color: "c4red",
    shape: "square",
    size: "md"
  }
});
const Badge = forwardRef(
  ({
    className,
    mode,
    color,
    shape,
    size,
    children,
    variant,
    borderColor,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: badgeVariants({
          mode,
          color,
          shape,
          size,
          variant,
          borderColor,
          className
        }),
        ref,
        ...props,
        children
      }
    );
  }
);
Badge.displayName = "Badge";

function cn(...inputs) {
  return twMerge(clsx(inputs));
}
const isFilled = (iconProps) => iconProps.variant === "filled";
const isThin = (iconProps) => iconProps.variant === "thin";
const overrideStroke = (iconProps) => isFilled(iconProps) ? "white" : "currentColor";

const colors$1 = {
  black: "text-black",
  inherit: "text-inherit",
  primary: "text-primary",
  sharemaroon: "text-sharemaroon",
  white: "text-white",
  sharegraywhite: "text-sharegraywhite",
  sharedarkblue: "text-sharedarkblue",
  nightblack: "text-nightblack",
  sharedeepblue: "text-sharedeepblue",
  "blue-600": "text-blue-600",
  "c4blue-400": "text-c4blue-400",
  "gray-25": "text-grey-25",
  "c4blue-600": "text-c4blue-600",
  "gray-200": "text-gray-200",
  "gray-300": "text-gray-300",
  "gray-500": "text-gray-500",
  "gray-600": "text-gray-600",
  "c4red-400": "text-c4red-400",
  "green-400": "text-green-400",
  "green-500": "text-green-500",
  "green-600": "text-green-600",
  "red-400": "text-red-400",
  "gold-400": "text-gold-400",
  "red-500": "text-red-500",
  "red-600": "text-red-600",
  "persia-400": "text-persia-400"
};

const iconVariants = cva("", {
  variants: {
    size: {
      lg: "size-xl",
      md: "size-lg",
      sm: "size-md",
      xs: "size-sm",
      custom: ""
    },
    variant: {
      thin: "stroke-1",
      thick: "stroke-2",
      strong: "stroke-[3px]",
      filled: ""
    },
    color: colors$1
  },
  compoundVariants: [
    {
      variant: "filled",
      className: "stroke-2"
    }
  ],
  defaultVariants: {
    color: "black",
    size: "md",
    variant: "thick"
  }
});

const SvgIcon = ({
  title,
  height,
  color,
  variant,
  size,
  width,
  testId,
  viewBox,
  children,
  className
}) => {
  const fill = { fill: variant === "filled" ? "currentColor" : "none" };
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: cn(iconVariants({ color, size, variant, className })),
      "data-testid": testId,
      height,
      stroke: "currentColor",
      viewBox,
      width,
      ...fill,
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx("title", { children: title }),
        children
      ]
    }
  );
};

const ArrowUp = (props) => {
  return /* @__PURE__ */ jsx(SvgIcon, { title: "ArrowUp", viewBox: "0 0 13 13", ...props, children: /* @__PURE__ */ jsx(
    "path",
    {
      clipRule: "evenodd",
      d: "M-2.8545e-07 6.46967L5.5 0.96967C5.79289 0.676777 6.26777 0.676777 6.56066 0.96967L12.0607 6.46967L11 7.53033L6.78033 3.31066L6.78033 13L5.28033 13L5.28033 3.31066L1.06066 7.53033L-2.8545e-07 6.46967Z",
      fill: "currentColor",
      fillRule: "evenodd"
    }
  ) });
};

const CaretDownIcon = (props) => {
  return /* @__PURE__ */ jsx(SvgIcon, { title: "CaretDown", viewBox: "0 0 24 24", ...props, children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M19.5 9L12 16.5L4.5 9",
      fill: "none",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ) });
};

const CaretUpIcon = (props) => {
  return /* @__PURE__ */ jsx(SvgIcon, { title: "CaretUp", viewBox: "0 0 24 24", ...props, children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M4.5 15L12 7.5L19.5 15",
      fill: "none",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ) });
};

const CarouselLeftIcon = (props) => {
  return /* @__PURE__ */ jsx(SvgIcon, { title: "CarouselLeft", viewBox: "0 0 48 48", ...props, children: /* @__PURE__ */ jsxs("g", { id: "Property 1=active, Property 2=left", children: [
    /* @__PURE__ */ jsx(
      "circle",
      {
        cx: "24",
        cy: "24",
        fill: "white",
        id: "Ellipse 1",
        r: "23.5"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M19 23.99L28.28 14L29 14.7L20.37 23.99L28.99 33.3L28.27 34L19 23.99Z",
        id: "Vector"
      }
    )
  ] }) });
};

const CarouselRightIcon = (props) => {
  return /* @__PURE__ */ jsx(SvgIcon, { title: "CarouselRight", viewBox: "0 0 48 48", ...props, children: /* @__PURE__ */ jsxs("g", { id: "Property 1=active, Property 2=right", children: [
    /* @__PURE__ */ jsx(
      "circle",
      {
        cx: "24",
        cy: "24",
        fill: "white",
        id: "Ellipse 2",
        r: "23.5"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M29 23.99L19.72 14L19 14.7L27.63 23.99L19.01 33.3L19.73 34L29 23.99Z",
        id: "Vector"
      }
    )
  ] }) });
};

const CheckIcon = (props) => {
  return /* @__PURE__ */ jsx(SvgIcon, { height: "8", viewBox: "-2 0 15 8", width: "15", ...props, children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M9.44824 1.75L3.94821 7L1.19824 4.375",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ) });
};

const CircleCheckIcon = (props) => {
  return /* @__PURE__ */ jsxs(SvgIcon, { height: "24", viewBox: "0 0 24 24", width: "24", ...props, children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M16.125 9.75L10.625 15L7.875 12.375",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  ] });
};

const ClockCounter = (props) => {
  return /* @__PURE__ */ jsxs(SvgIcon, { title: "counter", viewBox: "0 0 16 16", ...props, children: [
    /* @__PURE__ */ jsx("path", { d: "M8 5V8", strokeLinecap: "round", strokeLinejoin: "round" }),
    /* @__PURE__ */ jsx("path", { d: "M10.5981 9.5L8 8", strokeLinecap: "round", strokeLinejoin: "round" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M4.49023 6.23242H1.99023V3.73242",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M4.11155 11.8891C4.88074 12.6583 5.86075 13.1821 6.92764 13.3943C7.99454 13.6065 9.10041 13.4976 10.1054 13.0813C11.1104 12.6651 11.9694 11.9601 12.5737 11.0556C13.1781 10.1512 13.5006 9.0878 13.5006 8C13.5006 6.91221 13.1781 5.84884 12.5737 4.94437C11.9694 4.0399 11.1104 3.33495 10.1054 2.91867C9.10041 2.50238 7.99454 2.39346 6.92764 2.60568C5.86075 2.8179 4.88074 3.34173 4.11155 4.11092L1.99023 6.23224",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  ] });
};

const CrossIcon = (props) => {
  return /* @__PURE__ */ jsxs(SvgIcon, { title: "Cross", viewBox: "0 0 24 24", ...props, children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M18.75 5.25L5.25 18.75",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M18.75 18.75L5.25 5.25",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  ] });
};

const EyeIcon = (props) => {
  return /* @__PURE__ */ jsxs(SvgIcon, { title: "Eye", viewBox: "0 0 24 24", ...props, children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M12 5.24927C4.5 5.24927 1.5 12 1.5 12C1.5 12 4.5 18.7493 12 18.7493C19.5 18.7493 22.5 12 22.5 12C22.5 12 19.5 5.24927 12 5.24927Z",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M12 15.75C14.0711 15.75 15.75 14.0711 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75Z",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  ] });
};

const EyeSlashIcon = (props) => {
  return /* @__PURE__ */ jsxs(SvgIcon, { title: "EyeSlash", viewBox: "0 0 24 24", ...props, children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M4.5 3.75L19.5 20.25",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M14.5226 14.7747C13.7866 15.4436 12.8151 15.7928 11.8217 15.7454C10.8284 15.6981 9.89446 15.2581 9.22548 14.5222C8.5565 13.7863 8.20723 12.8149 8.25448 11.8215C8.30174 10.8281 8.74167 9.89414 9.47749 9.2251",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M6.93698 6.43066C3.11486 8.36618 1.5 12 1.5 12C1.5 12 4.5 18.7493 12 18.7493C13.7572 18.7633 15.4926 18.3585 17.0623 17.5685",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M19.5571 15.8531C21.6011 14.0224 22.5 12 22.5 12C22.5 12 19.5 5.24928 12 5.24928C11.3504 5.24822 10.7019 5.30103 10.061 5.40717",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M12.7057 8.31641C13.5027 8.46946 14.2287 8.8768 14.7746 9.47735C15.3205 10.0779 15.657 10.8393 15.7336 11.6472",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    )
  ] });
};

const FilledCrossIcon = (props) => /* @__PURE__ */ jsxs(SvgIcon, { title: "FilledCross", viewBox: "0 0 24 24", ...props, children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M15 9L9 15",
      stroke: overrideStroke(props),
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M15 15L9 9",
      stroke: overrideStroke(props),
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  )
] });

const FilledInfoIcon = (props) => {
  return /* @__PURE__ */ jsx(SvgIcon, { viewBox: "0 0 20 20", ...props, children: /* @__PURE__ */ jsx("path", { d: "M10 1.875C8.39303 1.875 6.82214 2.35152 5.486 3.24431C4.14985 4.1371 3.10844 5.40605 2.49348 6.8907C1.87852 8.37535 1.71762 10.009 2.03112 11.5851C2.34463 13.1612 3.11846 14.6089 4.25476 15.7452C5.39106 16.8815 6.8388 17.6554 8.4149 17.9689C9.99099 18.2824 11.6247 18.1215 13.1093 17.5065C14.594 16.8916 15.8629 15.8502 16.7557 14.514C17.6485 13.1779 18.125 11.607 18.125 10C18.1226 7.84586 17.2658 5.78065 15.7426 4.25745C14.2193 2.73425 12.1541 1.87744 10 1.875ZM9.8438 5.62503C10.0292 5.62503 10.2105 5.68001 10.3646 5.78303C10.5188 5.88604 10.639 6.03246 10.7099 6.20376C10.7809 6.37507 10.7995 6.56357 10.7633 6.74543C10.7271 6.92728 10.6378 7.09433 10.5067 7.22544C10.3756 7.35655 10.2085 7.44584 10.0267 7.48201C9.84484 7.51819 9.65634 7.49962 9.48503 7.42867C9.31372 7.35771 9.16731 7.23755 9.06429 7.08337C8.96128 6.9292 8.9063 6.74795 8.9063 6.56253C8.9063 6.31389 9.00507 6.07543 9.18088 5.89962C9.3567 5.7238 9.59516 5.62503 9.8438 5.62503ZM10.625 14.375H10C9.83425 14.375 9.67528 14.3091 9.55807 14.1919C9.44087 14.0747 9.37502 13.9158 9.37501 13.75V10C9.20925 10 9.05028 9.93415 8.93307 9.81694C8.81586 9.69973 8.75001 9.54076 8.75001 9.375C8.75001 9.20924 8.81586 9.05027 8.93307 8.93306C9.05028 8.81585 9.20925 8.75 9.37501 8.75H10C10.1658 8.75001 10.3247 8.81586 10.4419 8.93307C10.5591 9.05028 10.625 9.20924 10.625 9.375V13.125C10.7908 13.125 10.9497 13.1908 11.0669 13.3081C11.1842 13.4253 11.25 13.5842 11.25 13.75C11.25 13.9158 11.1842 14.0747 11.0669 14.1919C10.9497 14.3091 10.7908 14.375 10.625 14.375Z" }) });
};

const HintIcon = (props) => /* @__PURE__ */ jsx(SvgIcon, { title: "Hint", viewBox: "0 0 20 20", ...props, children: /* @__PURE__ */ jsx("path", { d: "M10 1.875C8.39303 1.875 6.82214 2.35152 5.486 3.24431C4.14985 4.1371 3.10844 5.40605 2.49348 6.8907C1.87852 8.37535 1.71762 10.009 2.03112 11.5851C2.34463 13.1612 3.11846 14.6089 4.25476 15.7452C5.39106 16.8815 6.8388 17.6554 8.4149 17.9689C9.99099 18.2824 11.6247 18.1215 13.1093 17.5065C14.594 16.8916 15.8629 15.8502 16.7557 14.514C17.6485 13.1779 18.125 11.607 18.125 10C18.1226 7.84586 17.2658 5.78065 15.7426 4.25744C14.2194 2.73424 12.1541 1.87743 10 1.875ZM9.375 6.25C9.375 6.08424 9.44085 5.92527 9.55806 5.80806C9.67527 5.69085 9.83424 5.625 10 5.625C10.1658 5.625 10.3247 5.69085 10.4419 5.80806C10.5592 5.92527 10.625 6.08424 10.625 6.25V10.625C10.625 10.7908 10.5592 10.9497 10.4419 11.0669C10.3247 11.1842 10.1658 11.25 10 11.25C9.83424 11.25 9.67527 11.1842 9.55806 11.0669C9.44085 10.9497 9.375 10.7908 9.375 10.625V6.25ZM10 14.375C9.81458 14.375 9.63333 14.32 9.47916 14.217C9.32499 14.114 9.20482 13.9676 9.13387 13.7963C9.06291 13.625 9.04434 13.4365 9.08052 13.2546C9.11669 13.0727 9.20598 12.9057 9.33709 12.7746C9.4682 12.6435 9.63525 12.5542 9.81711 12.518C9.99896 12.4818 10.1875 12.5004 10.3588 12.5714C10.5301 12.6423 10.6765 12.7625 10.7795 12.9167C10.8825 13.0708 10.9375 13.2521 10.9375 13.4375C10.9375 13.6861 10.8387 13.9246 10.6629 14.1004C10.4871 14.2762 10.2486 14.375 10 14.375Z" }) });

const InfoIcon = (props) => {
  const fill = isThin(props) ? "none" : "currentColor";
  return /* @__PURE__ */ jsxs(SvgIcon, { viewBox: "0 0 24 24", ...props, children: [
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M11.25 11.25H12V16.5H12.75",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M11.8125 9C12.4338 9 12.9375 8.49632 12.9375 7.875C12.9375 7.25368 12.4338 6.75 11.8125 6.75C11.1912 6.75 10.6875 7.25368 10.6875 7.875C10.6875 8.49632 11.1912 9 11.8125 9Z",
        fill,
        strokeWidth: 1
      }
    )
  ] });
};

const MagnifyingGlass = (props) => /* @__PURE__ */ jsxs(SvgIcon, { title: "MagnifyingGlass", viewBox: "0 0 24 24", ...props, children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M10.875 18.75C15.2242 18.75 18.75 15.2242 18.75 10.875C18.75 6.52576 15.2242 3 10.875 3C6.52576 3 3 6.52576 3 10.875C3 15.2242 6.52576 18.75 10.875 18.75Z",
      fill: "none",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M16.4431 16.4438L20.9994 21.0002",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  )
] });

const TickIcon = (props) => {
  return /* @__PURE__ */ jsx(SvgIcon, { title: "TickIcon", viewBox: "0 0 20 20", ...props, children: /* @__PURE__ */ jsx(
    "path",
    {
      d: "M16.875 5.625L8.125 14.3746L3.75 10",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ) });
};

const WarningIcon = (props) => {
  return /* @__PURE__ */ jsxs(SvgIcon, { height: "24", viewBox: "0 0 24 24", width: "24", ...props, children: [
    /* @__PURE__ */ jsx("path", { d: "M12 9.75V13.5", strokeLinecap: "round", strokeLinejoin: "round" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M10.7035 3.74857L2.4557 17.9978C2.32372 18.2258 2.25412 18.4846 2.25391 18.748C2.25369 19.0115 2.32286 19.2703 2.45446 19.4986C2.58605 19.7268 2.77544 19.9163 3.00355 20.0481C3.23167 20.1799 3.49047 20.2493 3.75391 20.2493H20.2495C20.5129 20.2493 20.7717 20.1799 20.9998 20.0481C21.2279 19.9163 21.4173 19.7268 21.5489 19.4986C21.6805 19.2703 21.7497 19.0115 21.7495 18.748C21.7492 18.4846 21.6796 18.2258 21.5477 17.9978L13.2999 3.74857C13.1681 3.52093 12.9788 3.33193 12.751 3.20055C12.5231 3.06916 12.2647 3 12.0017 3C11.7387 3 11.4803 3.06916 11.2524 3.20055C11.0245 3.33193 10.8352 3.52093 10.7035 3.74857V3.74857Z",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }
    ),
    /* @__PURE__ */ jsx("path", { d: "M12 18C12.6213 18 13.125 17.4963 13.125 16.875C13.125 16.2537 12.6213 15.75 12 15.75C11.3787 15.75 10.875 16.2537 10.875 16.875C10.875 17.4963 11.3787 18 12 18Z" })
  ] });
};

const WarningOctagonIcon = (props) => {
  return /* @__PURE__ */ jsxs(SvgIcon, { height: "24", viewBox: "0 0 24 24", width: "24", ...props, children: [
    /* @__PURE__ */ jsx("path", { d: "M12 7.5V12.75", strokeLinecap: "round", strokeLinejoin: "round" }),
    /* @__PURE__ */ jsx(
      "path",
      {
        d: "M15.4173 3H8.58274C8.48425 3 8.38672 3.0194 8.29573 3.05709C8.20473 3.09478 8.12205 3.15003 8.05241 3.21967L3.21967 8.05241C3.15003 8.12205 3.09478 8.20473 3.05709 8.29573C3.0194 8.38672 3 8.48425 3 8.58274V15.4173C3 15.5158 3.0194 15.6133 3.05709 15.7043C3.09478 15.7953 3.15003 15.8779 3.21967 15.9476L8.05241 20.7803C8.12205 20.85 8.20473 20.9052 8.29573 20.9429C8.38672 20.9806 8.48425 21 8.58274 21H15.4173C15.5158 21 15.6133 20.9806 15.7043 20.9429C15.7953 20.9052 15.8779 20.85 15.9476 20.7803L20.7803 15.9476C20.85 15.8779 20.9052 15.7953 20.9429 15.7043C20.9806 15.6133 21 15.5158 21 15.4173V8.58274C21 8.48425 20.9806 8.38672 20.9429 8.29573C20.9052 8.20473 20.85 8.12205 20.7803 8.05241L15.9476 3.21967C15.8779 3.15003 15.7953 3.09478 15.7043 3.05709C15.6133 3.0194 15.5158 3 15.4173 3Z",
        strokeMiterlimit: "10"
      }
    ),
    /* @__PURE__ */ jsx("path", { d: "M12 17.25C12.6213 17.25 13.125 16.7463 13.125 16.125C13.125 15.5037 12.6213 15 12 15C11.3787 15 10.875 15.5037 10.875 16.125C10.875 16.7463 11.3787 17.25 12 17.25Z" })
  ] });
};

const Typography = forwardRef(
  function Typography2(props, ref) {
    const {
      children,
      as = "div",
      className,
      testId,
      variant = "md",
      weight = "regular",
      fontStyle,
      htmlFor,
      style
    } = props;
    return createElement(
      as,
      {
        className: cn$1(
          {
            "text-4xl leading-10": variant === "4xl",
            "text-3xl leading-9": variant === "3xl",
            "text-2xl leading-8": variant === "2xl",
            "text-xl leading-7": variant === "xl",
            "text-lg leading-5": variant === "lg",
            "text-md leading-5": variant === "md",
            "text-sm leading-4": variant === "sm",
            "text-xs leading-4": variant === "xs",
            "text-2xs leading-3": variant === "2xs",
            "font-regular": weight === "regular",
            "font-medium": weight === "medium",
            "font-semiBold": weight === "semiBold",
            "font-bold": weight === "bold",
            italic: fontStyle === "italic",
            underline: fontStyle === "underline"
          },
          className
        ),
        ...htmlFor ? { htmlFor } : {},
        ...style ? { style } : {},
        ...testId ? { "data-testid": testId } : {},
        ...ref ? { ref } : {}
      },
      children
    );
  }
);

const ClockBadge = ({
  text,
  className
}) => {
  return /* @__PURE__ */ jsx(
    Badge,
    {
      borderColor: "grey",
      className,
      color: "transparent",
      shape: "roundedFull",
      size: "sm",
      variant: "outlined",
      children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-xs whitespace-nowrap", children: [
        /* @__PURE__ */ jsx(ClockCounter, { height: 16, size: "custom", width: 16 }),
        /* @__PURE__ */ jsx(Typography, { variant: "md", children: text })
      ] })
    }
  );
};
ClockBadge.displayName = "ClockBadge";

const bulletVariants = cva("rounded-full", {
  variants: {
    variant: {
      small: "size-3xs",
      medium: "size-2xs",
      large: "size-xs"
    },
    color: {
      black: "bg-black",
      primary: "bg-primary"
    }
  }
});

const Bullet = ({
  variant,
  color
}) => /* @__PURE__ */ jsx("div", { className: bulletVariants({ variant, color }) });

const cardVariants = cva("bg-white p-md", {
  variants: {
    variant: {
      soft: "md:rounded-lg md:border md:border-gray-100",
      rounded: "mb-md rounded-lg border-2 border-gray-100 py-[12px] md:mb-xs"
    }
  },
  defaultVariants: {
    variant: "soft"
  }
});

const Card = ({
  children,
  className,
  variant,
  testId = "card-container"
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn$1(cardVariants({ variant }), className),
      "data-testid": testId,
      children
    }
  );
};

const FormatTextToJSX = ({
  description,
  href,
  specialStyles = {}
}) => {
  if (!description) return [];
  const parts = description.split(
    /(\^\^[^^]+\^\^|\*\*[^*]+\*\*|\*[^*]+\*|<b>[^<]+<\/b>)/g
  );
  return parts.map((part, index) => {
    const key = `part-${index}`;
    if (part.startsWith("^^") && part.endsWith("^^")) {
      return /* @__PURE__ */ jsx(
        "a",
        {
          className: "text-sm font-bold text-primary",
          href,
          rel: "noopener noreferrer",
          style: specialStyles,
          target: "_blank",
          children: part.slice(2, -2)
        },
        key
      );
    } else if (part.startsWith("**") && part.endsWith("**")) {
      return /* @__PURE__ */ jsx("strong", { style: specialStyles, children: part.slice(2, -2) }, key);
    } else if (part.startsWith("*") && part.endsWith("*")) {
      return /* @__PURE__ */ jsx("strong", { style: specialStyles, children: part.slice(1, -1) }, key);
    } else if (part.startsWith("<b>") && part.endsWith("</b>")) {
      return /* @__PURE__ */ jsx("strong", { style: specialStyles, children: part.slice(3, -4) }, key);
    }
    return /* @__PURE__ */ jsx("span", { className: "whitespace-pre-wrap", children: part }, key);
  });
};

const FormFieldHelperText = ({
  disabled = false,
  error = false,
  helperText = "",
  success = false,
  testId
}) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn$1("gap-2xs flex items-center", disabled ? "opacity-40" : ""),
      "data-testid": testId,
      children: [
        error || success ? /* @__PURE__ */ jsx(
          HintIcon,
          {
            className: cn$1("stroke-0", {
              "text-red-500": error,
              "text-green-600": success
            }),
            height: 20,
            size: "custom",
            variant: "filled",
            width: 20
          }
        ) : /* @__PURE__ */ jsx(
          FilledInfoIcon,
          {
            className: "stroke-0 text-gray-500",
            height: 20,
            size: "custom",
            variant: "filled",
            width: 20
          }
        ),
        /* @__PURE__ */ jsx(
          Typography,
          {
            className: cn$1(
              "text-gray-500",
              { "text-red-500": error },
              { "text-green-600": success }
            ),
            variant: "sm",
            children: helperText
          }
        )
      ]
    }
  );
};

const textFieldInputVariants = cva(
  "peer h-2xl w-full rounded-lg border border-gray-100 bg-white px-md py-xs text-sm leading-4 outline-none focus:border-gray-300 focus:shadow-[0_0_4px_0px_rgba(0,0,0,0.1)] md:h-3xl md:text-lg md:leading-5",
  {
    variants: {
      disabled: {
        true: "bg-gray-50"
      },
      error: {
        true: "focus:shadow-[0_0_4px_0px_rgba(206, 54, 55, 0.25)] border-red-500 focus:border-red-500"
      },
      haveBoldText: {
        true: "font-semiBold"
      },
      success: {
        true: "focus:shadow-[0_0_4px_0px_rgba(38, 121, 73, 1)] border-green-600 focus:border-green-600"
      }
    }
  }
);
const textFieldLabelVariants = cva(
  "text-sm leading-4 text-black md:text-md md:leading-5",
  {
    variants: {
      error: {
        true: "text-red-500"
      },
      success: {
        true: "text-green-600"
      }
    }
  }
);

const TextField = forwardRef(
  ({
    autoFocusEnabled = false,
    className,
    defaultValue,
    disabled = false,
    error = false,
    floatingElement,
    haveBoldText = false,
    helperText,
    icon = false,
    iconClassName,
    iconPosition = "right",
    id,
    inputClassName,
    type = "text",
    label = "",
    labelClassName,
    onChange,
    placeholder = "",
    success = false,
    testId,
    ...rest
  }, ref) => {
    const [inputValue, setInputValue] = useState(defaultValue ?? "");
    const inputRef = useRef(null);
    const handleInputChange = (event) => {
      const input = event.target.value;
      setInputValue(input);
      if (onChange) {
        onChange(event);
      }
    };
    useEffect(() => {
      setInputValue(defaultValue ?? "");
    }, [defaultValue]);
    useEffect(() => {
      if (autoFocusEnabled) {
        inputRef.current?.focus();
      }
    }, [autoFocusEnabled]);
    const hasLabel = label || placeholder;
    return /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn$1(
          "relative w-full box-border flex flex-col gap-2xs",
          className
        ),
        ...testId && { "data-testid": `${testId}-wrapper` },
        children: [
          hasLabel ? /* @__PURE__ */ jsx(
            "label",
            {
              className: cn$1(
                textFieldLabelVariants({ error, success }),
                {
                  "sr-only": !label
                },
                labelClassName
              ),
              htmlFor: id,
              children: label || placeholder
            }
          ) : null,
          /* @__PURE__ */ jsxs("div", { className: "relative", children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                "aria-invalid": error,
                className: cn$1(
                  textFieldInputVariants({
                    disabled,
                    error,
                    haveBoldText,
                    success
                  }),
                  { "pl-2xl": iconPosition === "left" },
                  { "pr-2xl": iconPosition === "right" },
                  inputClassName
                ),
                "data-testid": testId,
                disabled,
                id,
                onChange: handleInputChange,
                placeholder,
                ref: (node) => {
                  inputRef.current = node;
                  if (typeof ref === "function") {
                    ref(node);
                  } else if (ref) {
                    ref.current = node;
                  }
                },
                type,
                value: inputValue,
                ...rest
              }
            ),
            icon ? /* @__PURE__ */ jsx(
              "div",
              {
                className: cn$1(
                  "absolute top-1/2 -translate-y-1/2 flex items-center",
                  {
                    "left-sm": iconPosition === "left",
                    "right-sm": iconPosition === "right"
                  },
                  iconClassName
                ),
                children: icon
              }
            ) : null,
            floatingElement ? floatingElement : null
          ] }),
          helperText ? /* @__PURE__ */ jsx(
            FormFieldHelperText,
            {
              disabled,
              error,
              helperText,
              success,
              ...testId && { testId: `${testId}-helper` }
            }
          ) : null
        ]
      }
    );
  }
);
TextField.displayName = "TextField";

const lineSeparatorVariants = cva("flex grow", {
  variants: {
    variant: {
      thin: "h-px",
      regular: "h-3xs",
      thick: "h-2xs",
      thicker: "h-1.5xs"
    },
    color: {
      primary: "bg-primary",
      "gray-50": "bg-gray-50",
      "polo-25": "bg-polo-25"
    }
  }
});

const LineSeparator = ({
  className,
  variant,
  color
}) => /* @__PURE__ */ jsx("div", { className: cn$1(lineSeparatorVariants({ variant, color }), className) });

const Marquee = ({ children, className }) => {
  return /* @__PURE__ */ jsx("div", { className: cn$1("flex w-full overflow-x-hidden", className), children: /* @__PURE__ */ jsx("div", { className: "animate-marquee whitespace-nowrap", children }) });
};

const Overlay = ({
  className = "",
  withBlur = false
}) => {
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);
  if (isServer()) return null;
  return createPortal(
    /* @__PURE__ */ jsx(
      "div",
      {
        className: cn$1(
          "fixed inset-0 z-[60] bg-transparent pointer-events-auto",
          { "backdrop-blur-sm": withBlur },
          className
        ),
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
        role: "presentation"
      }
    ),
    document.body
  );
};

const LinearProgressBar = ({ className, showOverlay = true }) => {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    showOverlay ? /* @__PURE__ */ jsx(
      "div",
      {
        className: "absolute inset-0 bg-white opacity-50",
        "data-testid": "progress-overlay"
      }
    ) : null,
    /* @__PURE__ */ jsx(
      "div",
      {
        className: cn$1(
          "absolute bottom-0 inset-x-0 h-[3px] w-full overflow-hidden bg-c4blue-100",
          className
        ),
        children: /* @__PURE__ */ jsx("div", { className: "size-full animate-progress-linear bg-primary" })
      }
    )
  ] });
};

const ProductImage = ({
  imageUrl,
  isGrayScale = false,
  alt,
  className
}) => {
  return /* @__PURE__ */ jsx(
    "img",
    {
      alt,
      className: cn$1(
        "rounded-lg object-contain",
        {
          grayscale: isGrayScale
        },
        className
      ),
      src: imageUrl
    }
  );
};

const progressVariants = cva(`w-full rounded-l-none`, {
  variants: {
    color: {
      blue: "bg-striped-gradient bg-[length:10px_10px] bg-repeat"
    },
    size: {
      md: "h-xs"
    }
  },
  defaultVariants: {
    color: "blue",
    size: "md"
  }
});
const ProgressBar = ({
  animated = false,
  progress = 0,
  color = "blue",
  size = "md",
  className,
  testId = "progress-bar"
}) => {
  const maxProgress = 100;
  const clampedProgress = Math.min(Math.max(progress, 0), maxProgress);
  return /* @__PURE__ */ jsx(
    "div",
    {
      "aria-valuemax": 100,
      "aria-valuemin": 0,
      "aria-valuenow": clampedProgress,
      className: cn$1(
        "relative w-full overflow-hidden bg-polo-50 rtl:rotate-180",
        className
      ),
      "data-testid": `${testId}-container`,
      role: "progressbar",
      children: /* @__PURE__ */ jsx(
        "div",
        {
          className: cn$1(
            progressVariants({ color, size }),
            clampedProgress === 100 ? "rounded-r-none" : "rounded-r-full",
            {
              "animate-strips": animated
            }
          ),
          "data-testid": `${testId}-indicator`,
          style: {
            transform: `translateX(-${maxProgress - clampedProgress}%)`
          }
        }
      )
    }
  );
};

const RangeSlider = ({
  min,
  max,
  onChange,
  className,
  defaultRange,
  isLTR
}) => {
  const [minVal, setMinVal] = useState(min);
  const [maxVal, setMaxVal] = useState(max);
  const minValRef = useRef(null);
  const maxValRef = useRef(null);
  const range = useRef(null);
  const [defaultMin, defaultMax] = defaultRange;
  const getPercent = useCallback(
    (value) => {
      const percent = Math.round(
        (value - defaultMin) / (defaultMax - defaultMin) * 100
      );
      return Math.min(100, Math.max(0, percent));
    },
    [defaultMax, defaultMin]
  );
  useEffect(() => {
    const minPercent = getPercent(minVal);
    const maxPercent = getPercent(maxVal);
    if (range.current) {
      if (isLTR) {
        range.current.style.left = `${minPercent}%`;
      } else {
        range.current.style.right = `${minPercent}%`;
      }
      range.current.style.width = `${maxPercent - minPercent}%`;
    }
    onChange(minVal, maxVal);
  }, [minVal, maxVal, getPercent, onChange, isLTR]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn$1(
        "relative z-20 flex size-full items-center justify-between overflow-hidden",
        className
      ),
      "data-testid": "price-range-slider",
      children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            className: cn$1(
              "thumb w-full pointer-events-none absolute z-50 h-0 outline-none bg-primary left-0",
              { "z-50": minVal > max - 100 }
            ),
            max: defaultMax,
            min: defaultMin,
            onChange: (event) => {
              const value = Math.min(Number(event.target.value), maxVal - 1);
              setMinVal(value);
              event.target.value = value.toString();
            },
            ref: minValRef,
            type: "range",
            value: minVal
          }
        ),
        /* @__PURE__ */ jsx(
          "input",
          {
            className: "thumb pointer-events-none absolute left-0 z-50 h-0 w-full bg-primary outline-none",
            max: defaultMax,
            min: defaultMin,
            onChange: (event) => {
              const value = Math.max(Number(event.target.value), minVal + 1);
              setMaxVal(value);
              event.target.value = value.toString();
            },
            ref: maxValRef,
            type: "range",
            value: maxVal
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "relative w-full", children: [
          /* @__PURE__ */ jsx("div", { className: "absolute h-[5px] w-full rounded-full bg-gray-100" }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: "absolute h-[5px] max-w-full rounded-full bg-primary",
              ref: range
            }
          )
        ] })
      ]
    }
  );
};

const ScrollToTop = () => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return /* @__PURE__ */ jsx(Fragment, { children: showScrollButton ? /* @__PURE__ */ jsx(
    Button,
    {
      className: "fixed bottom-20 right-md rounded-full border-[3px] border-solid border-primary bg-white p-xs shadow-lg",
      onClick: handleScrollToTop,
      variant: "ghost",
      children: /* @__PURE__ */ jsx(ArrowUp, { className: "stroke-1", color: "primary", size: "sm" })
    }
  ) : null });
};

const selectTriggerVariants = cva(
  "flex w-full items-center gap-2xs focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-40 rtl:flex-row-reverse",
  {
    variants: {
      variant: {
        classic: "h-2xl justify-between rounded-lg border border-gray-100 bg-white px-md py-xs text-sm md:h-3xl md:text-lg",
        enhanced: "relative justify-center rounded-full border-2 border-white bg-primary p-xs text-sm font-bold text-white"
      },
      error: {
        true: "border-red-500"
      },
      focusedOrOpen: {
        true: ""
      }
    },
    defaultVariants: {
      variant: "classic"
    }
  }
);
const selectContentVariants = cva(
  "relative z-50 max-h-[300px] w-full overflow-scroll bg-white shadow-lg data-[side=top]:-translate-y-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  {
    variants: {
      variant: {
        classic: "mt-xs rounded-2xl p-lg shadow-[0_4px_30px_0px_rgba(0,0,0,0.15)]",
        enhanced: "rounded-b-xl"
      }
    },
    defaultVariants: {
      variant: "classic"
    }
  }
);
const selectItemVariants = cva(
  "relative flex cursor-pointer gap-xs bg-white py-sm outline-none data-[disabled]:pointer-events-none",
  {
    variants: {
      variant: {
        classic: "justify-start border-b border-gray-100 p-md text-lg text-black hover:bg-secondary hover:font-semiBold hover:text-primary focus:bg-secondary focus:font-semiBold focus:text-primary data-[state=checked]:bg-secondary data-[state=checked]:font-semiBold data-[state=checked]:text-primary",
        enhanced: "my-xs justify-center text-md font-bold hover:bg-c4blue-25 hover:text-primary focus:bg-c4blue-25 focus:text-primary data-[state=checked]:bg-c4blue-25 data-[state=checked]:text-primary"
      },
      centerAlignedOptions: {
        true: "justify-center"
      }
    },
    defaultVariants: {
      variant: "classic"
    }
  }
);

const SelectTrigger = ({
  children,
  className,
  disabled,
  error,
  formattedLabel = "",
  label,
  value,
  variant,
  open = false
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const iconProps = variant === "enhanced" ? { color: "primary", size: "sm", variant: "strong" } : { color: "gray-500", size: "md", variant: "thick" };
  useEffect(() => {
    setIsFocused(Boolean(value));
  }, [value]);
  return /* @__PURE__ */ jsxs("div", { className: "relative flex w-full flex-col gap-2xs", children: [
    label && variant !== "enhanced" ? /* @__PURE__ */ jsx(
      Typography,
      {
        className: cn$1("text-sm md:text-md leading-4 md:leading-5", {
          "opacity-50": disabled,
          "text-red-500": error
        }),
        children: label
      }
    ) : null,
    /* @__PURE__ */ jsxs(
      Trigger,
      {
        className: cn$1(
          selectTriggerVariants({
            focusedOrOpen: isFocused || open,
            variant,
            error
          }),
          className
        ),
        children: [
          formattedLabel || children,
          /* @__PURE__ */ jsx(Icon, { asChild: true, children: open ? /* @__PURE__ */ jsx(CaretUpIcon, { ...iconProps }) : /* @__PURE__ */ jsx(CaretDownIcon, { ...iconProps }) })
        ]
      }
    )
  ] });
};
SelectTrigger.displayName = "SelectTrigger";

const SelectContent = ({
  children,
  position = "popper",
  variant
}) => {
  return /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx("div", { className: "c4c-web", children: /* @__PURE__ */ jsx(
    Content,
    {
      className: cn$1(
        selectContentVariants({ variant }),
        "w-[var(--radix-select-trigger-width)]"
      ),
      position,
      children: /* @__PURE__ */ jsx(Viewport, { children })
    }
  ) }) });
};
SelectContent.displayName = "SelectContent";

const SelectItem = ({
  centerAlignedOptions,
  children,
  value,
  variant,
  spanClassName
}) => {
  return /* @__PURE__ */ jsx(
    Item,
    {
      className: cn$1(selectItemVariants({ centerAlignedOptions, variant })),
      value,
      children: /* @__PURE__ */ jsx("span", { className: spanClassName, children: /* @__PURE__ */ jsx(ItemText, { children }) })
    }
  );
};
SelectItem.displayName = "SelectItem";

const Select = ({
  centerAlignedOptions = false,
  className,
  disabled,
  error = false,
  formattedLabel = "",
  helperText = "",
  label = "",
  options,
  value = "",
  variant = "classic",
  spanClassName,
  onValueChange
}) => {
  const [open, setOpen] = useState(false);
  return /* @__PURE__ */ jsxs("div", { className: "w-full", children: [
    /* @__PURE__ */ jsxs(
      Root,
      {
        disabled,
        onOpenChange: setOpen,
        onValueChange,
        open,
        value,
        children: [
          /* @__PURE__ */ jsx(
            SelectTrigger,
            {
              className,
              disabled,
              error,
              formattedLabel,
              label,
              open,
              value,
              variant,
              children: /* @__PURE__ */ jsx(Value, {})
            }
          ),
          /* @__PURE__ */ jsx(SelectContent, { variant, children: options?.map((option) => /* @__PURE__ */ jsx(
            SelectItem,
            {
              centerAlignedOptions,
              spanClassName,
              value: option.value,
              variant,
              children: option.label
            },
            option.value
          )) })
        ]
      }
    ),
    helperText ? /* @__PURE__ */ jsx(
      FormFieldHelperText,
      {
        disabled,
        error,
        helperText
      }
    ) : null
  ] });
};

var Ct=s=>{switch(s){case"success":return $t;case"info":return _t;case"warning":return Wt;case"error":return Ut;default:return null}},Ft=Array(12).fill(0),It=({visible:s})=>e__default.createElement("div",{className:"sonner-loading-wrapper","data-visible":s},e__default.createElement("div",{className:"sonner-spinner"},Ft.map((o,t)=>e__default.createElement("div",{className:"sonner-loading-bar",key:`spinner-bar-${t}`})))),$t=e__default.createElement("svg",{xmlns:"http://www.w3.org/2000/svg",viewBox:"0 0 20 20",fill:"currentColor",height:"20",width:"20"},e__default.createElement("path",{fillRule:"evenodd",d:"M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z",clipRule:"evenodd"})),Wt=e__default.createElement("svg",{xmlns:"http://www.w3.org/2000/svg",viewBox:"0 0 24 24",fill:"currentColor",height:"20",width:"20"},e__default.createElement("path",{fillRule:"evenodd",d:"M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z",clipRule:"evenodd"})),_t=e__default.createElement("svg",{xmlns:"http://www.w3.org/2000/svg",viewBox:"0 0 20 20",fill:"currentColor",height:"20",width:"20"},e__default.createElement("path",{fillRule:"evenodd",d:"M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z",clipRule:"evenodd"})),Ut=e__default.createElement("svg",{xmlns:"http://www.w3.org/2000/svg",viewBox:"0 0 20 20",fill:"currentColor",height:"20",width:"20"},e__default.createElement("path",{fillRule:"evenodd",d:"M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z",clipRule:"evenodd"}));var Dt=()=>{let[s,o]=e__default.useState(document.hidden);return e__default.useEffect(()=>{let t=()=>{o(document.hidden);};return document.addEventListener("visibilitychange",t),()=>window.removeEventListener("visibilitychange",t)},[]),s};var ct=1,ut=class{constructor(){this.subscribe=o=>(this.subscribers.push(o),()=>{let t=this.subscribers.indexOf(o);this.subscribers.splice(t,1);});this.publish=o=>{this.subscribers.forEach(t=>t(o));};this.addToast=o=>{this.publish(o),this.toasts=[...this.toasts,o];};this.create=o=>{var b;let{message:t,...n}=o,h=typeof(o==null?void 0:o.id)=="number"||((b=o.id)==null?void 0:b.length)>0?o.id:ct++,u=this.toasts.find(d=>d.id===h),g=o.dismissible===void 0?!0:o.dismissible;return u?this.toasts=this.toasts.map(d=>d.id===h?(this.publish({...d,...o,id:h,title:t}),{...d,...o,id:h,dismissible:g,title:t}):d):this.addToast({title:t,...n,dismissible:g,id:h}),h};this.dismiss=o=>(o||this.toasts.forEach(t=>{this.subscribers.forEach(n=>n({id:t.id,dismiss:!0}));}),this.subscribers.forEach(t=>t({id:o,dismiss:!0})),o);this.message=(o,t)=>this.create({...t,message:o});this.error=(o,t)=>this.create({...t,message:o,type:"error"});this.success=(o,t)=>this.create({...t,type:"success",message:o});this.info=(o,t)=>this.create({...t,type:"info",message:o});this.warning=(o,t)=>this.create({...t,type:"warning",message:o});this.loading=(o,t)=>this.create({...t,type:"loading",message:o});this.promise=(o,t)=>{if(!t)return;let n;t.loading!==void 0&&(n=this.create({...t,promise:o,type:"loading",message:t.loading,description:typeof t.description!="function"?t.description:void 0}));let h=o instanceof Promise?o:o(),u=n!==void 0;return h.then(async g=>{if(Ot(g)&&!g.ok){u=!1;let b=typeof t.error=="function"?await t.error(`HTTP error! status: ${g.status}`):t.error,d=typeof t.description=="function"?await t.description(`HTTP error! status: ${g.status}`):t.description;this.create({id:n,type:"error",message:b,description:d});}else if(t.success!==void 0){u=!1;let b=typeof t.success=="function"?await t.success(g):t.success,d=typeof t.description=="function"?await t.description(g):t.description;this.create({id:n,type:"success",message:b,description:d});}}).catch(async g=>{if(t.error!==void 0){u=!1;let b=typeof t.error=="function"?await t.error(g):t.error,d=typeof t.description=="function"?await t.description(g):t.description;this.create({id:n,type:"error",message:b,description:d});}}).finally(()=>{var g;u&&(this.dismiss(n),n=void 0),(g=t.finally)==null||g.call(t);}),n};this.custom=(o,t)=>{let n=(t==null?void 0:t.id)||ct++;return this.create({jsx:o(n),id:n,...t}),n};this.subscribers=[],this.toasts=[];}},v=new ut,Vt=(s,o)=>{let t=(o==null?void 0:o.id)||ct++;return v.addToast({title:s,...o,id:t}),t},Ot=s=>s&&typeof s=="object"&&"ok"in s&&typeof s.ok=="boolean"&&"status"in s&&typeof s.status=="number",Kt=Vt,Xt=()=>v.toasts,Jt=Object.assign(Kt,{success:v.success,info:v.info,warning:v.warning,error:v.error,custom:v.custom,message:v.message,promise:v.promise,dismiss:v.dismiss,loading:v.loading},{getHistory:Xt});function ft(s,{insertAt:o}={}){if(typeof document=="undefined")return;let t=document.head||document.getElementsByTagName("head")[0],n=document.createElement("style");n.type="text/css",o==="top"&&t.firstChild?t.insertBefore(n,t.firstChild):t.appendChild(n),n.styleSheet?n.styleSheet.cssText=s:n.appendChild(document.createTextNode(s));}ft(`:where(html[dir="ltr"]),:where([data-sonner-toaster][dir="ltr"]){--toast-icon-margin-start: -3px;--toast-icon-margin-end: 4px;--toast-svg-margin-start: -1px;--toast-svg-margin-end: 0px;--toast-button-margin-start: auto;--toast-button-margin-end: 0;--toast-close-button-start: 0;--toast-close-button-end: unset;--toast-close-button-transform: translate(-35%, -35%)}:where(html[dir="rtl"]),:where([data-sonner-toaster][dir="rtl"]){--toast-icon-margin-start: 4px;--toast-icon-margin-end: -3px;--toast-svg-margin-start: 0px;--toast-svg-margin-end: -1px;--toast-button-margin-start: 0;--toast-button-margin-end: auto;--toast-close-button-start: unset;--toast-close-button-end: 0;--toast-close-button-transform: translate(35%, -35%)}:where([data-sonner-toaster]){position:fixed;width:var(--width);font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica Neue,Arial,Noto Sans,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol,Noto Color Emoji;--gray1: hsl(0, 0%, 99%);--gray2: hsl(0, 0%, 97.3%);--gray3: hsl(0, 0%, 95.1%);--gray4: hsl(0, 0%, 93%);--gray5: hsl(0, 0%, 90.9%);--gray6: hsl(0, 0%, 88.7%);--gray7: hsl(0, 0%, 85.8%);--gray8: hsl(0, 0%, 78%);--gray9: hsl(0, 0%, 56.1%);--gray10: hsl(0, 0%, 52.3%);--gray11: hsl(0, 0%, 43.5%);--gray12: hsl(0, 0%, 9%);--border-radius: 8px;box-sizing:border-box;padding:0;margin:0;list-style:none;outline:none;z-index:999999999}:where([data-sonner-toaster][data-x-position="right"]){right:max(var(--offset),env(safe-area-inset-right))}:where([data-sonner-toaster][data-x-position="left"]){left:max(var(--offset),env(safe-area-inset-left))}:where([data-sonner-toaster][data-x-position="center"]){left:50%;transform:translate(-50%)}:where([data-sonner-toaster][data-y-position="top"]){top:max(var(--offset),env(safe-area-inset-top))}:where([data-sonner-toaster][data-y-position="bottom"]){bottom:max(var(--offset),env(safe-area-inset-bottom))}:where([data-sonner-toast]){--y: translateY(100%);--lift-amount: calc(var(--lift) * var(--gap));z-index:var(--z-index);position:absolute;opacity:0;transform:var(--y);filter:blur(0);touch-action:none;transition:transform .4s,opacity .4s,height .4s,box-shadow .2s;box-sizing:border-box;outline:none;overflow-wrap:anywhere}:where([data-sonner-toast][data-styled="true"]){padding:16px;background:var(--normal-bg);border:1px solid var(--normal-border);color:var(--normal-text);border-radius:var(--border-radius);box-shadow:0 4px 12px #0000001a;width:var(--width);font-size:13px;display:flex;align-items:center;gap:6px}:where([data-sonner-toast]:focus-visible){box-shadow:0 4px 12px #0000001a,0 0 0 2px #0003}:where([data-sonner-toast][data-y-position="top"]){top:0;--y: translateY(-100%);--lift: 1;--lift-amount: calc(1 * var(--gap))}:where([data-sonner-toast][data-y-position="bottom"]){bottom:0;--y: translateY(100%);--lift: -1;--lift-amount: calc(var(--lift) * var(--gap))}:where([data-sonner-toast]) :where([data-description]){font-weight:400;line-height:1.4;color:inherit}:where([data-sonner-toast]) :where([data-title]){font-weight:500;line-height:1.5;color:inherit}:where([data-sonner-toast]) :where([data-icon]){display:flex;height:16px;width:16px;position:relative;justify-content:flex-start;align-items:center;flex-shrink:0;margin-left:var(--toast-icon-margin-start);margin-right:var(--toast-icon-margin-end)}:where([data-sonner-toast][data-promise="true"]) :where([data-icon])>svg{opacity:0;transform:scale(.8);transform-origin:center;animation:sonner-fade-in .3s ease forwards}:where([data-sonner-toast]) :where([data-icon])>*{flex-shrink:0}:where([data-sonner-toast]) :where([data-icon]) svg{margin-left:var(--toast-svg-margin-start);margin-right:var(--toast-svg-margin-end)}:where([data-sonner-toast]) :where([data-content]){display:flex;flex-direction:column;gap:2px}[data-sonner-toast][data-styled=true] [data-button]{border-radius:4px;padding-left:8px;padding-right:8px;height:24px;font-size:12px;color:var(--normal-bg);background:var(--normal-text);margin-left:var(--toast-button-margin-start);margin-right:var(--toast-button-margin-end);border:none;cursor:pointer;outline:none;display:flex;align-items:center;flex-shrink:0;transition:opacity .4s,box-shadow .2s}:where([data-sonner-toast]) :where([data-button]):focus-visible{box-shadow:0 0 0 2px #0006}:where([data-sonner-toast]) :where([data-button]):first-of-type{margin-left:var(--toast-button-margin-start);margin-right:var(--toast-button-margin-end)}:where([data-sonner-toast]) :where([data-cancel]){color:var(--normal-text);background:rgba(0,0,0,.08)}:where([data-sonner-toast][data-theme="dark"]) :where([data-cancel]){background:rgba(255,255,255,.3)}:where([data-sonner-toast]) :where([data-close-button]){position:absolute;left:var(--toast-close-button-start);right:var(--toast-close-button-end);top:0;height:20px;width:20px;display:flex;justify-content:center;align-items:center;padding:0;background:var(--gray1);color:var(--gray12);border:1px solid var(--gray4);transform:var(--toast-close-button-transform);border-radius:50%;cursor:pointer;z-index:1;transition:opacity .1s,background .2s,border-color .2s}:where([data-sonner-toast]) :where([data-close-button]):focus-visible{box-shadow:0 4px 12px #0000001a,0 0 0 2px #0003}:where([data-sonner-toast]) :where([data-disabled="true"]){cursor:not-allowed}:where([data-sonner-toast]):hover :where([data-close-button]):hover{background:var(--gray2);border-color:var(--gray5)}:where([data-sonner-toast][data-swiping="true"]):before{content:"";position:absolute;left:0;right:0;height:100%;z-index:-1}:where([data-sonner-toast][data-y-position="top"][data-swiping="true"]):before{bottom:50%;transform:scaleY(3) translateY(50%)}:where([data-sonner-toast][data-y-position="bottom"][data-swiping="true"]):before{top:50%;transform:scaleY(3) translateY(-50%)}:where([data-sonner-toast][data-swiping="false"][data-removed="true"]):before{content:"";position:absolute;inset:0;transform:scaleY(2)}:where([data-sonner-toast]):after{content:"";position:absolute;left:0;height:calc(var(--gap) + 1px);bottom:100%;width:100%}:where([data-sonner-toast][data-mounted="true"]){--y: translateY(0);opacity:1}:where([data-sonner-toast][data-expanded="false"][data-front="false"]){--scale: var(--toasts-before) * .05 + 1;--y: translateY(calc(var(--lift-amount) * var(--toasts-before))) scale(calc(-1 * var(--scale)));height:var(--front-toast-height)}:where([data-sonner-toast])>*{transition:opacity .4s}:where([data-sonner-toast][data-expanded="false"][data-front="false"][data-styled="true"])>*{opacity:0}:where([data-sonner-toast][data-visible="false"]){opacity:0;pointer-events:none}:where([data-sonner-toast][data-mounted="true"][data-expanded="true"]){--y: translateY(calc(var(--lift) * var(--offset)));height:var(--initial-height)}:where([data-sonner-toast][data-removed="true"][data-front="true"][data-swipe-out="false"]){--y: translateY(calc(var(--lift) * -100%));opacity:0}:where([data-sonner-toast][data-removed="true"][data-front="false"][data-swipe-out="false"][data-expanded="true"]){--y: translateY(calc(var(--lift) * var(--offset) + var(--lift) * -100%));opacity:0}:where([data-sonner-toast][data-removed="true"][data-front="false"][data-swipe-out="false"][data-expanded="false"]){--y: translateY(40%);opacity:0;transition:transform .5s,opacity .2s}:where([data-sonner-toast][data-removed="true"][data-front="false"]):before{height:calc(var(--initial-height) + 20%)}[data-sonner-toast][data-swiping=true]{transform:var(--y) translateY(var(--swipe-amount, 0px));transition:none}[data-sonner-toast][data-swipe-out=true][data-y-position=bottom],[data-sonner-toast][data-swipe-out=true][data-y-position=top]{animation:swipe-out .2s ease-out forwards}@keyframes swipe-out{0%{transform:translateY(calc(var(--lift) * var(--offset) + var(--swipe-amount)));opacity:1}to{transform:translateY(calc(var(--lift) * var(--offset) + var(--swipe-amount) + var(--lift) * -100%));opacity:0}}@media (max-width: 600px){[data-sonner-toaster]{position:fixed;--mobile-offset: 16px;right:var(--mobile-offset);left:var(--mobile-offset);width:100%}[data-sonner-toaster] [data-sonner-toast]{left:0;right:0;width:calc(100% - var(--mobile-offset) * 2)}[data-sonner-toaster][data-x-position=left]{left:var(--mobile-offset)}[data-sonner-toaster][data-y-position=bottom]{bottom:20px}[data-sonner-toaster][data-y-position=top]{top:20px}[data-sonner-toaster][data-x-position=center]{left:var(--mobile-offset);right:var(--mobile-offset);transform:none}}[data-sonner-toaster][data-theme=light]{--normal-bg: #fff;--normal-border: var(--gray4);--normal-text: var(--gray12);--success-bg: hsl(143, 85%, 96%);--success-border: hsl(145, 92%, 91%);--success-text: hsl(140, 100%, 27%);--info-bg: hsl(208, 100%, 97%);--info-border: hsl(221, 91%, 91%);--info-text: hsl(210, 92%, 45%);--warning-bg: hsl(49, 100%, 97%);--warning-border: hsl(49, 91%, 91%);--warning-text: hsl(31, 92%, 45%);--error-bg: hsl(359, 100%, 97%);--error-border: hsl(359, 100%, 94%);--error-text: hsl(360, 100%, 45%)}[data-sonner-toaster][data-theme=light] [data-sonner-toast][data-invert=true]{--normal-bg: #000;--normal-border: hsl(0, 0%, 20%);--normal-text: var(--gray1)}[data-sonner-toaster][data-theme=dark] [data-sonner-toast][data-invert=true]{--normal-bg: #fff;--normal-border: var(--gray3);--normal-text: var(--gray12)}[data-sonner-toaster][data-theme=dark]{--normal-bg: #000;--normal-border: hsl(0, 0%, 20%);--normal-text: var(--gray1);--success-bg: hsl(150, 100%, 6%);--success-border: hsl(147, 100%, 12%);--success-text: hsl(150, 86%, 65%);--info-bg: hsl(215, 100%, 6%);--info-border: hsl(223, 100%, 12%);--info-text: hsl(216, 87%, 65%);--warning-bg: hsl(64, 100%, 6%);--warning-border: hsl(60, 100%, 12%);--warning-text: hsl(46, 87%, 65%);--error-bg: hsl(358, 76%, 10%);--error-border: hsl(357, 89%, 16%);--error-text: hsl(358, 100%, 81%)}[data-rich-colors=true][data-sonner-toast][data-type=success],[data-rich-colors=true][data-sonner-toast][data-type=success] [data-close-button]{background:var(--success-bg);border-color:var(--success-border);color:var(--success-text)}[data-rich-colors=true][data-sonner-toast][data-type=info],[data-rich-colors=true][data-sonner-toast][data-type=info] [data-close-button]{background:var(--info-bg);border-color:var(--info-border);color:var(--info-text)}[data-rich-colors=true][data-sonner-toast][data-type=warning],[data-rich-colors=true][data-sonner-toast][data-type=warning] [data-close-button]{background:var(--warning-bg);border-color:var(--warning-border);color:var(--warning-text)}[data-rich-colors=true][data-sonner-toast][data-type=error],[data-rich-colors=true][data-sonner-toast][data-type=error] [data-close-button]{background:var(--error-bg);border-color:var(--error-border);color:var(--error-text)}.sonner-loading-wrapper{--size: 16px;height:var(--size);width:var(--size);position:absolute;inset:0;z-index:10}.sonner-loading-wrapper[data-visible=false]{transform-origin:center;animation:sonner-fade-out .2s ease forwards}.sonner-spinner{position:relative;top:50%;left:50%;height:var(--size);width:var(--size)}.sonner-loading-bar{animation:sonner-spin 1.2s linear infinite;background:var(--gray11);border-radius:6px;height:8%;left:-10%;position:absolute;top:-3.9%;width:24%}.sonner-loading-bar:nth-child(1){animation-delay:-1.2s;transform:rotate(.0001deg) translate(146%)}.sonner-loading-bar:nth-child(2){animation-delay:-1.1s;transform:rotate(30deg) translate(146%)}.sonner-loading-bar:nth-child(3){animation-delay:-1s;transform:rotate(60deg) translate(146%)}.sonner-loading-bar:nth-child(4){animation-delay:-.9s;transform:rotate(90deg) translate(146%)}.sonner-loading-bar:nth-child(5){animation-delay:-.8s;transform:rotate(120deg) translate(146%)}.sonner-loading-bar:nth-child(6){animation-delay:-.7s;transform:rotate(150deg) translate(146%)}.sonner-loading-bar:nth-child(7){animation-delay:-.6s;transform:rotate(180deg) translate(146%)}.sonner-loading-bar:nth-child(8){animation-delay:-.5s;transform:rotate(210deg) translate(146%)}.sonner-loading-bar:nth-child(9){animation-delay:-.4s;transform:rotate(240deg) translate(146%)}.sonner-loading-bar:nth-child(10){animation-delay:-.3s;transform:rotate(270deg) translate(146%)}.sonner-loading-bar:nth-child(11){animation-delay:-.2s;transform:rotate(300deg) translate(146%)}.sonner-loading-bar:nth-child(12){animation-delay:-.1s;transform:rotate(330deg) translate(146%)}@keyframes sonner-fade-in{0%{opacity:0;transform:scale(.8)}to{opacity:1;transform:scale(1)}}@keyframes sonner-fade-out{0%{opacity:1;transform:scale(1)}to{opacity:0;transform:scale(.8)}}@keyframes sonner-spin{0%{opacity:1}to{opacity:.15}}@media (prefers-reduced-motion){[data-sonner-toast],[data-sonner-toast]>*,.sonner-loading-bar{transition:none!important;animation:none!important}}.sonner-loader{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);transform-origin:center;transition:opacity .2s,transform .2s}.sonner-loader[data-visible=false]{opacity:0;transform:scale(.8) translate(-50%,-50%)}
`);function U(s){return s.label!==void 0}var qt=3,Qt="32px",Zt=4e3,te=356,ee=14,oe=20,ae=200;function ne(...s){return s.filter(Boolean).join(" ")}var se=s=>{var yt,xt,vt,wt,Tt,St,Rt,Et,Nt,Pt;let{invert:o,toast:t,unstyled:n,interacting:h,setHeights:u,visibleToasts:g,heights:b,index:d,toasts:q,expanded:$,removeToast:V,defaultRichColors:Q,closeButton:i,style:O,cancelButtonStyle:K,actionButtonStyle:Z,className:tt="",descriptionClassName:et="",duration:X,position:ot,gap:w,loadingIcon:j,expandByDefault:W,classNames:r,icons:I,closeButtonAriaLabel:at="Close toast",pauseWhenPageIsHidden:k,cn:T}=s,[z,nt]=e__default.useState(!1),[D,H]=e__default.useState(!1),[st,N]=e__default.useState(!1),[M,rt]=e__default.useState(!1),[c,m]=e__default.useState(0),[y,S]=e__default.useState(0),A=e__default.useRef(null),l=e__default.useRef(null),_=d===0,J=d+1<=g,x=t.type,P=t.dismissible!==!1,Mt=t.className||"",At=t.descriptionClassName||"",G=e__default.useMemo(()=>b.findIndex(a=>a.toastId===t.id)||0,[b,t.id]),Lt=e__default.useMemo(()=>{var a;return (a=t.closeButton)!=null?a:i},[t.closeButton,i]),mt=e__default.useMemo(()=>t.duration||X||Zt,[t.duration,X]),it=e__default.useRef(0),Y=e__default.useRef(0),pt=e__default.useRef(0),F=e__default.useRef(null),[gt,zt]=ot.split("-"),ht=e__default.useMemo(()=>b.reduce((a,f,p)=>p>=G?a:a+f.height,0),[b,G]),bt=Dt(),jt=t.invert||o,lt=x==="loading";Y.current=e__default.useMemo(()=>G*w+ht,[G,ht]),e__default.useEffect(()=>{nt(!0);},[]),e__default.useLayoutEffect(()=>{if(!z)return;let a=l.current,f=a.style.height;a.style.height="auto";let p=a.getBoundingClientRect().height;a.style.height=f,S(p),u(B=>B.find(R=>R.toastId===t.id)?B.map(R=>R.toastId===t.id?{...R,height:p}:R):[{toastId:t.id,height:p,position:t.position},...B]);},[z,t.title,t.description,u,t.id]);let L=e__default.useCallback(()=>{H(!0),m(Y.current),u(a=>a.filter(f=>f.toastId!==t.id)),setTimeout(()=>{V(t);},ae);},[t,V,u,Y]);e__default.useEffect(()=>{if(t.promise&&x==="loading"||t.duration===1/0||t.type==="loading")return;let a,f=mt;return $||h||k&&bt?(()=>{if(pt.current<it.current){let C=new Date().getTime()-it.current;f=f-C;}pt.current=new Date().getTime();})():(()=>{f!==1/0&&(it.current=new Date().getTime(),a=setTimeout(()=>{var C;(C=t.onAutoClose)==null||C.call(t,t),L();},f));})(),()=>clearTimeout(a)},[$,h,W,t,mt,L,t.promise,x,k,bt]),e__default.useEffect(()=>{let a=l.current;if(a){let f=a.getBoundingClientRect().height;return S(f),u(p=>[{toastId:t.id,height:f,position:t.position},...p]),()=>u(p=>p.filter(B=>B.toastId!==t.id))}},[u,t.id]),e__default.useEffect(()=>{t.delete&&L();},[L,t.delete]);function Yt(){return I!=null&&I.loading?e__default.createElement("div",{className:"sonner-loader","data-visible":x==="loading"},I.loading):j?e__default.createElement("div",{className:"sonner-loader","data-visible":x==="loading"},j):e__default.createElement(It,{visible:x==="loading"})}return e__default.createElement("li",{"aria-live":t.important?"assertive":"polite","aria-atomic":"true",role:"status",tabIndex:0,ref:l,className:T(tt,Mt,r==null?void 0:r.toast,(yt=t==null?void 0:t.classNames)==null?void 0:yt.toast,r==null?void 0:r.default,r==null?void 0:r[x],(xt=t==null?void 0:t.classNames)==null?void 0:xt[x]),"data-sonner-toast":"","data-rich-colors":(vt=t.richColors)!=null?vt:Q,"data-styled":!(t.jsx||t.unstyled||n),"data-mounted":z,"data-promise":!!t.promise,"data-removed":D,"data-visible":J,"data-y-position":gt,"data-x-position":zt,"data-index":d,"data-front":_,"data-swiping":st,"data-dismissible":P,"data-type":x,"data-invert":jt,"data-swipe-out":M,"data-expanded":!!($||W&&z),style:{"--index":d,"--toasts-before":d,"--z-index":q.length-d,"--offset":`${D?c:Y.current}px`,"--initial-height":W?"auto":`${y}px`,...O,...t.style},onPointerDown:a=>{lt||!P||(A.current=new Date,m(Y.current),a.target.setPointerCapture(a.pointerId),a.target.tagName!=="BUTTON"&&(N(!0),F.current={x:a.clientX,y:a.clientY}));},onPointerUp:()=>{var B,C,R,dt;if(M||!P)return;F.current=null;let a=Number(((B=l.current)==null?void 0:B.style.getPropertyValue("--swipe-amount").replace("px",""))||0),f=new Date().getTime()-((C=A.current)==null?void 0:C.getTime()),p=Math.abs(a)/f;if(Math.abs(a)>=oe||p>.11){m(Y.current),(R=t.onDismiss)==null||R.call(t,t),L(),rt(!0);return}(dt=l.current)==null||dt.style.setProperty("--swipe-amount","0px"),N(!1);},onPointerMove:a=>{var Bt;if(!F.current||!P)return;let f=a.clientY-F.current.y,p=a.clientX-F.current.x,C=(gt==="top"?Math.min:Math.max)(0,f),R=a.pointerType==="touch"?10:2;Math.abs(C)>R?(Bt=l.current)==null||Bt.style.setProperty("--swipe-amount",`${f}px`):Math.abs(p)>R&&(F.current=null);}},Lt&&!t.jsx?e__default.createElement("button",{"aria-label":at,"data-disabled":lt,"data-close-button":!0,onClick:lt||!P?()=>{}:()=>{var a;L(),(a=t.onDismiss)==null||a.call(t,t);},className:T(r==null?void 0:r.closeButton,(wt=t==null?void 0:t.classNames)==null?void 0:wt.closeButton)},e__default.createElement("svg",{xmlns:"http://www.w3.org/2000/svg",width:"12",height:"12",viewBox:"0 0 24 24",fill:"none",stroke:"currentColor",strokeWidth:"1.5",strokeLinecap:"round",strokeLinejoin:"round"},e__default.createElement("line",{x1:"18",y1:"6",x2:"6",y2:"18"}),e__default.createElement("line",{x1:"6",y1:"6",x2:"18",y2:"18"}))):null,t.jsx||e__default.isValidElement(t.title)?t.jsx||t.title:e__default.createElement(e__default.Fragment,null,x||t.icon||t.promise?e__default.createElement("div",{"data-icon":"",className:T(r==null?void 0:r.icon,(Tt=t==null?void 0:t.classNames)==null?void 0:Tt.icon)},t.promise||t.type==="loading"&&!t.icon?t.icon||Yt():null,t.type!=="loading"?t.icon||(I==null?void 0:I[x])||Ct(x):null):null,e__default.createElement("div",{"data-content":"",className:T(r==null?void 0:r.content,(St=t==null?void 0:t.classNames)==null?void 0:St.content)},e__default.createElement("div",{"data-title":"",className:T(r==null?void 0:r.title,(Rt=t==null?void 0:t.classNames)==null?void 0:Rt.title)},t.title),t.description?e__default.createElement("div",{"data-description":"",className:T(et,At,r==null?void 0:r.description,(Et=t==null?void 0:t.classNames)==null?void 0:Et.description)},t.description):null),e__default.isValidElement(t.cancel)?t.cancel:t.cancel&&U(t.cancel)?e__default.createElement("button",{"data-button":!0,"data-cancel":!0,style:t.cancelButtonStyle||K,onClick:a=>{var f,p;U(t.cancel)&&P&&((p=(f=t.cancel).onClick)==null||p.call(f,a),L());},className:T(r==null?void 0:r.cancelButton,(Nt=t==null?void 0:t.classNames)==null?void 0:Nt.cancelButton)},t.cancel.label):null,e__default.isValidElement(t.action)?t.action:t.action&&U(t.action)?e__default.createElement("button",{"data-button":!0,"data-action":!0,style:t.actionButtonStyle||Z,onClick:a=>{var f,p;U(t.action)&&(a.defaultPrevented||((p=(f=t.action).onClick)==null||p.call(f,a),L()));},className:T(r==null?void 0:r.actionButton,(Pt=t==null?void 0:t.classNames)==null?void 0:Pt.actionButton)},t.action.label):null))};function Ht(){if(typeof window=="undefined"||typeof document=="undefined")return "ltr";let s=document.documentElement.getAttribute("dir");return s==="auto"||!s?window.getComputedStyle(document.documentElement).direction:s}var Te=s=>{let{invert:o,position:t="bottom-right",hotkey:n=["altKey","KeyT"],expand:h,closeButton:u,className:g,offset:b,theme:d="light",richColors:q,duration:$,style:V,visibleToasts:Q=qt,toastOptions:i,dir:O=Ht(),gap:K=ee,loadingIcon:Z,icons:tt,containerAriaLabel:et="Notifications",pauseWhenPageIsHidden:X,cn:ot=ne}=s,[w,j]=e__default.useState([]),W=e__default.useMemo(()=>Array.from(new Set([t].concat(w.filter(c=>c.position).map(c=>c.position)))),[w,t]),[r,I]=e__default.useState([]),[at,k]=e__default.useState(!1),[T,z]=e__default.useState(!1),[nt,D]=e__default.useState(d!=="system"?d:typeof window!="undefined"&&window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches?"dark":"light"),H=e__default.useRef(null),st=n.join("+").replace(/Key/g,"").replace(/Digit/g,""),N=e__default.useRef(null),M=e__default.useRef(!1),rt=e__default.useCallback(c=>{var m;(m=w.find(y=>y.id===c.id))!=null&&m.delete||v.dismiss(c.id),j(y=>y.filter(({id:S})=>S!==c.id));},[w]);return e__default.useEffect(()=>v.subscribe(c=>{if(c.dismiss){j(m=>m.map(y=>y.id===c.id?{...y,delete:!0}:y));return}setTimeout(()=>{Gt.flushSync(()=>{j(m=>{let y=m.findIndex(S=>S.id===c.id);return y!==-1?[...m.slice(0,y),{...m[y],...c},...m.slice(y+1)]:[c,...m]});});});}),[]),e__default.useEffect(()=>{if(d!=="system"){D(d);return}d==="system"&&(window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches?D("dark"):D("light")),typeof window!="undefined"&&window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change",({matches:c})=>{D(c?"dark":"light");});},[d]),e__default.useEffect(()=>{w.length<=1&&k(!1);},[w]),e__default.useEffect(()=>{let c=m=>{var S,A;n.every(l=>m[l]||m.code===l)&&(k(!0),(S=H.current)==null||S.focus()),m.code==="Escape"&&(document.activeElement===H.current||(A=H.current)!=null&&A.contains(document.activeElement))&&k(!1);};return document.addEventListener("keydown",c),()=>document.removeEventListener("keydown",c)},[n]),e__default.useEffect(()=>{if(H.current)return ()=>{N.current&&(N.current.focus({preventScroll:!0}),N.current=null,M.current=!1);}},[H.current]),w.length?e__default.createElement("section",{"aria-label":`${et} ${st}`,tabIndex:-1},W.map((c,m)=>{var A;let[y,S]=c.split("-");return e__default.createElement("ol",{key:c,dir:O==="auto"?Ht():O,tabIndex:-1,ref:H,className:g,"data-sonner-toaster":!0,"data-theme":nt,"data-y-position":y,"data-x-position":S,style:{"--front-toast-height":`${((A=r[0])==null?void 0:A.height)||0}px`,"--offset":typeof b=="number"?`${b}px`:b||Qt,"--width":`${te}px`,"--gap":`${K}px`,...V},onBlur:l=>{M.current&&!l.currentTarget.contains(l.relatedTarget)&&(M.current=!1,N.current&&(N.current.focus({preventScroll:!0}),N.current=null));},onFocus:l=>{l.target instanceof HTMLElement&&l.target.dataset.dismissible==="false"||M.current||(M.current=!0,N.current=l.relatedTarget);},onMouseEnter:()=>k(!0),onMouseMove:()=>k(!0),onMouseLeave:()=>{T||k(!1);},onPointerDown:l=>{l.target instanceof HTMLElement&&l.target.dataset.dismissible==="false"||z(!0);},onPointerUp:()=>z(!1)},w.filter(l=>!l.position&&m===0||l.position===c).map((l,_)=>{var J,x;return e__default.createElement(se,{key:l.id,icons:tt,index:_,toast:l,defaultRichColors:q,duration:(J=i==null?void 0:i.duration)!=null?J:$,className:i==null?void 0:i.className,descriptionClassName:i==null?void 0:i.descriptionClassName,invert:o,visibleToasts:Q,closeButton:(x=i==null?void 0:i.closeButton)!=null?x:u,interacting:T,position:c,style:i==null?void 0:i.style,unstyled:i==null?void 0:i.unstyled,classNames:i==null?void 0:i.classNames,cancelButtonStyle:i==null?void 0:i.cancelButtonStyle,actionButtonStyle:i==null?void 0:i.actionButtonStyle,removeToast:rt,toasts:w.filter(P=>P.position==l.position),heights:r.filter(P=>P.position==l.position),setHeights:I,expandByDefault:h,gap:K,loadingIcon:Z,expanded:at,pauseWhenPageIsHidden:X,cn:ot})}))})):null};

const Toaster = ({ ...props }) => {
  return /* @__PURE__ */ jsx(
    Te,
    {
      className: "toaster group",
      theme: "system",
      toastOptions: {
        classNames: {
          toast: "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground"
        }
      },
      ...props
    }
  );
};

const Spinner = ({
  className,
  color,
  size,
  showOverlay = false,
  testId
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn$1(spinnerVariants({ color, size, className })),
      ...testId ? { "data-testid": testId } : {},
      children: showOverlay ? /* @__PURE__ */ jsx(Overlay, {}) : null
    }
  );
};
const spinnerVariants = cva("animate-spin rounded-full", {
  variants: {
    size: {
      small: "size-[18px] border-2",
      medium: "size-lg border-4",
      large: "size-[42px] border-[5px]"
    },
    color: {
      primary: "border-primary border-t-transparent",
      white: "border-white border-t-transparent",
      green: "border-green-500 border-t-transparent",
      red: "border-red-500 border-t-transparent"
    }
  },
  defaultVariants: {
    size: "small",
    color: "primary"
  }
});

const textInputVariants = cva(
  "peer h-3xl w-full rounded-sm border border-gray-100 bg-white p-sm outline-none focus:border-2 focus:border-gray-500",
  {
    variants: {
      disabled: {
        true: "text-gray-300 opacity-40"
      },
      error: {
        true: "border-red-500 focus:border-red-500"
      },
      filled: {
        true: "border-gray-500 text-black"
      }
    },
    compoundVariants: [
      {
        filled: true,
        error: true,
        className: "border-red-500"
      }
    ]
  }
);
const textInputFloatingLabelVariants = cva(
  "absolute start-1 top-2 z-10 origin-[0] -translate-y-4 bg-white px-xs text-lg text-gray-500 duration-300 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:text-md peer-focus:text-black rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4",
  {
    variants: {
      filled: {
        true: "text-md text-black"
      },
      disabled: {
        true: "text-gray-100"
      },
      error: {
        true: "text-red-500 peer-focus:text-red-500"
      },
      success: {
        true: "text-green-600 peer-focus:text-green-600"
      }
    },
    compoundVariants: [
      {
        error: true,
        className: "text-red-500"
      },
      {
        disabled: true,
        className: "text-gray-300"
      },
      {
        error: true,
        filled: true,
        className: "text-red-500"
      }
    ]
  }
);

const TextInput = forwardRef(
  ({
    autoFocusEnabled = false,
    className,
    defaultValue,
    disabled = false,
    error,
    helperText,
    icon = false,
    iconPosition = "right",
    id = "floating_input",
    inputClassName,
    inputType = "text",
    label,
    name,
    onChange,
    onBlur,
    success = false,
    testId = "text-input",
    inputStyleClasses = {}
  }, ref) => {
    const [inputValue, setInputValue] = useState(defaultValue ?? "");
    const inputRef = useRef(null);
    const handleInputChange = (event) => {
      const input = event.target.value;
      setInputValue(input);
      if (onChange) {
        onChange(event);
      }
    };
    useEffect(() => {
      setInputValue(defaultValue ?? "");
    }, [defaultValue]);
    useEffect(() => {
      if (autoFocusEnabled) {
        inputRef.current?.focus();
      }
    }, [autoFocusEnabled]);
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: cn$1("relative w-full box-border ", className),
          "data-testid": `${testId}-wrapper`,
          children: [
            icon ? /* @__PURE__ */ jsx(
              "div",
              {
                className: cn$1("absolute top-md flex items-center", {
                  "left-sm": iconPosition === "left",
                  "right-sm": iconPosition === "right"
                }),
                children: icon
              }
            ) : null,
            /* @__PURE__ */ jsx(
              "input",
              {
                className: cn$1(
                  textInputVariants({
                    disabled,
                    error,
                    filled: Boolean(inputValue)
                  }),
                  { "pl-10": iconPosition === "left" },
                  inputClassName
                ),
                "data-testid": testId,
                disabled,
                id,
                name,
                onBlur,
                onChange: handleInputChange,
                placeholder: " ",
                ref: (node) => {
                  inputRef.current = node;
                  if (typeof ref === "function") {
                    ref(node);
                  } else if (ref) {
                    ref.current = node;
                  }
                },
                style: inputStyleClasses,
                type: inputType,
                value: inputValue
              }
            ),
            /* @__PURE__ */ jsx(
              "label",
              {
                className: cn$1(
                  textInputFloatingLabelVariants({
                    disabled,
                    error,
                    success,
                    filled: Boolean(inputValue)
                  })
                ),
                htmlFor: id,
                children: label
              }
            )
          ]
        }
      ),
      helperText ? /* @__PURE__ */ jsx(
        FormFieldHelperText,
        {
          disabled,
          error,
          helperText,
          success,
          testId: `${testId}-helper`
        }
      ) : null
    ] });
  }
);
TextInput.displayName = "TextInput";

const Toggle = ({
  className,
  checked = false,
  disabled,
  showIcon = false,
  testId = "toggle",
  onCheckedChange,
  ...props
}) => {
  const [isToggled, setIsToggled] = useState(checked);
  useEffect(() => {
    setIsToggled(checked);
  }, [checked]);
  const handleToggleChange = () => {
    setIsToggled((prev) => !prev);
    onCheckedChange?.(!isToggled);
  };
  return /* @__PURE__ */ jsx(
    SwitchPrimitives.Root,
    {
      className: cn$1(
        "peer inline-flex h-xl w-[52px] bg-primary shrink-0 cursor-pointer items-center rounded-full border-xs border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-15 data-[state=checked]:bg-primary data-[state=unchecked]:bg-primary/30",
        className
      ),
      ...props,
      checked: isToggled,
      "data-testid": testId,
      disabled,
      onCheckedChange: handleToggleChange,
      children: /* @__PURE__ */ jsx(
        SwitchPrimitives.Thumb,
        {
          className: cn$1(
            "pointer-events-none size-[28px] p-2xs flex items-center justify-center bg-white rounded-full shadow-lg ring-0 transition-transform ltr:data-[state=checked]:translate-x-[22px] ltr:data-[state=unchecked]:translate-x-[2px] rtl:data-[state=checked]:translate-x-[-22px] rtl:data-[state=unchecked]:translate-x-[-2px]"
          ),
          children: showIcon && isToggled ? /* @__PURE__ */ jsx(TickIcon, { color: "primary", "data-testid": `${testId}-icon` }) : null
        }
      )
    }
  );
};
Toggle.displayName = "Toggle";

const Skeleton = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn$1("animate-pulse rounded-md bg-muted bg-gray-50", className),
      ...props
    }
  );
};

const Accordion = Root$1;
const AccordionItem = e.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(Item$1, { className: cn$1("border-b", className), ref, ...props }));
AccordionItem.displayName = "AccordionItem";
const AccordionTrigger = e.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(Header, { className: "flex", children: /* @__PURE__ */ jsx(
  Trigger$1,
  {
    className: cn$1(
      "flex flex-1 items-center justify-between py-md transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
      className
    ),
    ref,
    ...props,
    children
  }
) }));
AccordionTrigger.displayName = Trigger$1.displayName;
const AccordionContent = e.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  Content$1,
  {
    className: "overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
    ref,
    ...props,
    children: /* @__PURE__ */ jsx("div", { className: cn$1("pb-md pt-0", className), children })
  }
));
AccordionContent.displayName = Content$1.displayName;

const CarouselContext = createContext(null);
function useCarousel() {
  const context = useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}
const Carousel = forwardRef(
  ({
    orientation = "horizontal",
    opts,
    setApi,
    plugins,
    className,
    children,
    ...props
  }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y"
      },
      plugins
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const onSelect = useCallback((apiData) => {
      if (!apiData) {
        return;
      }
      setCanScrollPrev(apiData.canScrollPrev());
      setCanScrollNext(apiData.canScrollNext());
    }, []);
    const scrollPrev = useCallback(() => {
      api?.scrollPrev();
    }, [api]);
    const scrollNext = useCallback(() => {
      api?.scrollNext();
    }, [api]);
    const scrollTo = useCallback(
      (index) => {
        api?.scrollTo(index);
      },
      [api]
    );
    const handleKeyDown = useCallback(
      (event) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext]
    );
    useEffect(() => {
      if (!api || !setApi) {
        return;
      }
      setApi(api);
    }, [api, setApi]);
    useEffect(() => {
      if (!api) {
        return;
      }
      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);
      return () => {
        api.off("select", onSelect);
      };
    }, [api, onSelect]);
    return /* @__PURE__ */ jsx(
      CarouselContext.Provider,
      {
        value: {
          carouselRef,
          api,
          opts,
          orientation,
          scrollPrev,
          scrollNext,
          scrollTo,
          canScrollPrev,
          canScrollNext
        },
        children: /* @__PURE__ */ jsx(
          "div",
          {
            "aria-roledescription": "carousel",
            className: cn$1("relative", className),
            onKeyDownCapture: handleKeyDown,
            ref,
            role: "region",
            ...props,
            children
          }
        )
      }
    );
  }
);
Carousel.displayName = "Carousel";

const CarouselContent = forwardRef(({ className, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();
  return /* @__PURE__ */ jsx("div", { className: "overflow-hidden", ref: carouselRef, children: /* @__PURE__ */ jsx(
    "div",
    {
      className: cn$1(
        "flex",
        orientation === "horizontal" ? "-ml-md" : "-mt-md flex-col",
        className
      ),
      ref,
      ...props
    }
  ) });
});
CarouselContent.displayName = "CarouselContent";

const CarouselItem = ({
  className,
  ...props
}) => {
  const { orientation } = useCarousel();
  return /* @__PURE__ */ jsx(
    "div",
    {
      "aria-roledescription": "slide",
      className: cn$1(
        "min-w-0 shrink-0 grow-0 basis-full",
        orientation === "horizontal" ? "ml-md" : "mt-md",
        className
      ),
      role: "group",
      ...props
    }
  );
};
CarouselItem.displayName = "CarouselItem";

const CarouselNext = ({
  className
}) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel();
  const color = canScrollNext ? "gray-600" : "gray-300";
  return /* @__PURE__ */ jsxs(
    "button",
    {
      className: cn$1(
        "absolute h-xs w-xs rounded-full",
        orientation === "horizontal" ? "right-md top-1/2 -translate-y-1/2" : "-bottom-3xl left-1/2 -translate-x-1/2 rotate-90",
        className
      ),
      disabled: !canScrollNext,
      onClick: scrollNext,
      type: "button",
      children: [
        /* @__PURE__ */ jsx(
          CarouselRightIcon,
          {
            color,
            height: 48,
            size: "custom",
            variant: "thin",
            width: 48
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Next slide" })
      ]
    }
  );
};
CarouselNext.displayName = "CarouselNext";

const CarouselPrevious = ({
  className
}) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();
  const color = canScrollPrev ? "gray-600" : "gray-300";
  return /* @__PURE__ */ jsxs(
    "button",
    {
      className: cn$1(
        "absolute  h-xs w-xs rounded-full",
        orientation === "horizontal" ? "-left-md top-1/2 -translate-y-1/2" : "-top-3xl left-1/2 -translate-x-1/2 rotate-90",
        className
      ),
      disabled: !canScrollPrev,
      onClick: scrollPrev,
      type: "button",
      children: [
        /* @__PURE__ */ jsx(
          CarouselLeftIcon,
          {
            color,
            height: 48,
            size: "custom",
            variant: "thin",
            width: 48
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Previous slide" })
      ]
    }
  );
};
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselScroll = ({
  imageIndex
}) => {
  const { scrollTo } = useCarousel();
  useEffect(() => {
    scrollTo?.(imageIndex);
  }, [imageIndex, scrollTo]);
  return null;
};
CarouselScroll.displayName = "CarouselScroll";

const checkboxVariants = cva(
  "peer size-md shrink-0 rounded-xs border-sm shadow focus-visible:outline-none disabled:cursor-not-allowed disabled:border-gray-500 disabled:opacity-50",
  {
    variants: {
      variant: {
        primary: "text-primary data-[state=checked]:bg-primary",
        outline: "text-primary",
        filled: "text-gray-500 data-[state=checked]:bg-primary data-[state=checked]:text-primary"
      }
    },
    defaultVariants: {
      variant: "outline"
    }
  }
);

const ShadCnCheckbox = e.forwardRef(({ className, variant, ...props }, ref) => /* @__PURE__ */ jsx(
  CheckboxPrimitive.Root,
  {
    className: cn$1(checkboxVariants({ variant }), className),
    ref,
    ...props,
    children: /* @__PURE__ */ jsx(CheckboxPrimitive.Indicator, { className: "text-current flex items-start justify-center", children: /* @__PURE__ */ jsx(
      CheckIcon,
      {
        color: "primary",
        size: "xs",
        ...variant === "primary" && { color: "white" },
        ...variant === "filled" && {
          size: "custom",
          height: 12,
          width: 12,
          color: "white"
        }
      }
    ) })
  }
));
ShadCnCheckbox.displayName = "ShadCnCheckbox";
const Checkbox = ({
  checked,
  onCheckedChange,
  isLoading = false,
  label,
  disabled,
  fieldId = "",
  testId = "",
  isLabelBeforeCheckbox = false,
  variant,
  className,
  weight
}) => {
  const checkboxSnippet = isLoading ? /* @__PURE__ */ jsx(Spinner, { showOverlay: true }) : /* @__PURE__ */ jsx(
    ShadCnCheckbox,
    {
      checked,
      "data-testid": testId,
      disabled,
      id: fieldId,
      onCheckedChange,
      variant
    }
  );
  return /* @__PURE__ */ jsx("div", { className: "space-xs flex items-center", children: label ? /* @__PURE__ */ jsx(
    Typography,
    {
      as: "label",
      className: cn$1(
        "flex items-center gap-xs align-middle",
        {
          "cursor-not-allowed opacity-70": disabled,
          "cursor-pointer": !disabled
        },
        className
      ),
      htmlFor: fieldId,
      testId: `label-${testId}`,
      variant: "sm",
      ...weight && { weight },
      children: isLabelBeforeCheckbox ? /* @__PURE__ */ jsxs(Fragment, { children: [
        label,
        checkboxSnippet
      ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
        checkboxSnippet,
        label
      ] })
    }
  ) : checkboxSnippet });
};
Checkbox.displayName = "Checkbox";

var EPlacement = /* @__PURE__ */ ((EPlacement2) => {
  EPlacement2["Left"] = "left";
  EPlacement2["Right"] = "right";
  return EPlacement2;
})(EPlacement || {});
var EOrientation = /* @__PURE__ */ ((EOrientation2) => {
  EOrientation2["Horizontal"] = "horizontal";
  EOrientation2["Vertical"] = "vertical";
  return EOrientation2;
})(EOrientation || {});
var EShape = /* @__PURE__ */ ((EShape2) => {
  EShape2["Square"] = "square";
  EShape2["Rounded"] = "rounded";
  return EShape2;
})(EShape || {});
var ESize = /* @__PURE__ */ ((ESize2) => {
  ESize2["XS"] = "xs";
  ESize2["SM"] = "sm";
  ESize2["MD"] = "md";
  ESize2["LG"] = "lg";
  return ESize2;
})(ESize || {});

const chipVariants = cva(
  "flex cursor-pointer items-center justify-center gap-xs bg-white text-md font-bold hover:bg-primary/10 hover:text-primary",
  {
    variants: {
      variant: {
        solid: "bg-primary/5 text-primary hover:bg-primary hover:text-white",
        outline: "border border-solid border-gray-100 hover:border-primary",
        ghost: ""
      },
      size: {
        xs: "px-xs",
        sm: "px-xs py-2xs",
        md: "px-sm py-xs",
        lg: "px-md py-sm"
      },
      shape: {
        square: "rounded-xl",
        rounded: "rounded-full"
      },
      orientation: {
        horizontal: "flex-row",
        vertical: "flex-col"
      },
      selected: {
        true: ""
      },
      disabled: {
        true: "pointer-events-none opacity-40"
      }
    },
    compoundVariants: [
      {
        selected: true,
        variant: "solid",
        className: "bg-primary text-white"
      },
      {
        selected: true,
        variant: "outline",
        className: "border-primary bg-primary/5 text-primary"
      },
      {
        selected: true,
        variant: "ghost",
        className: "bg-primary/5 text-primary"
      }
    ],
    defaultVariants: {
      variant: "ghost"
    }
  }
);

const handleSelect = (disabled, onSelect) => () => {
  if (!disabled && onSelect) {
    onSelect();
  }
};
const ChipInput = ({ selected, disabled, multiSelect, value, onChange }) => /* @__PURE__ */ jsx(
  "input",
  {
    checked: selected,
    className: "hidden",
    disabled,
    name: "chip",
    onChange,
    type: multiSelect ? "checkbox" : "radio",
    value
  }
);
const ChipItem = forwardRef(function ChipItem2({
  className,
  selectedChipItemClassName,
  disabled = false,
  icon,
  iconPlacement = EPlacement.Left,
  label,
  multiSelect = false,
  orientation = EOrientation.Horizontal,
  selected = false,
  shape = EShape.Square,
  showIconOnlyWhenSelected = false,
  size = ESize.SM,
  value,
  variant = "ghost",
  onSelect
}, selectedChipItemRef) {
  const shouldDisplayIcon = icon && (selected || !showIconOnlyWhenSelected);
  const shouldDisplayLeftIcon = shouldDisplayIcon && iconPlacement === EPlacement.Left;
  const shouldDisplayRightIcon = shouldDisplayIcon && iconPlacement === EPlacement.Right;
  const onChange = handleSelect(disabled, onSelect);
  return /* @__PURE__ */ jsxs(
    Typography,
    {
      as: "label",
      className: cn$1(
        chipVariants({
          variant,
          size,
          shape,
          orientation,
          selected,
          disabled
        }),
        className,
        selected && selectedChipItemClassName
      ),
      ref: selectedChipItemRef,
      weight: "bold",
      children: [
        /* @__PURE__ */ jsx(
          ChipInput,
          {
            disabled,
            multiSelect,
            onChange,
            selected,
            value
          }
        ),
        shouldDisplayLeftIcon ? icon : null,
        label,
        shouldDisplayRightIcon ? icon : null
      ]
    }
  );
});

const Chip = forwardRef(function Chip2({
  chipItemClassName,
  selectedChipItemClassName,
  chips,
  className,
  disabled = false,
  icon,
  showIconOnlyWhenSelected = false,
  iconPlacement = EPlacement.Left,
  multiSelect = false,
  orientation = EOrientation.Horizontal,
  selectedValues = [],
  shape = EShape.Square,
  size = ESize.SM,
  variant = "ghost",
  onSelectionChange
}, selectedChipItemRef) {
  const [selectedChips, setSelectedChips] = useState(selectedValues);
  const handleSelect = (value) => {
    let updatedSelectedChips;
    if (multiSelect) {
      updatedSelectedChips = selectedChips.includes(value) ? selectedChips.filter((chip) => chip !== value) : [...selectedChips, value];
      setSelectedChips(updatedSelectedChips);
      onSelectionChange(updatedSelectedChips);
    } else {
      updatedSelectedChips = [value];
      setSelectedChips(updatedSelectedChips);
      onSelectionChange(updatedSelectedChips);
    }
  };
  return /* @__PURE__ */ jsx("div", { className, role: "list", children: chips.map((chip) => /* @__PURE__ */ jsx(
    ChipItem,
    {
      className: chipItemClassName,
      disabled: chip.disabled ?? disabled,
      icon,
      iconPlacement,
      label: chip.label,
      multiSelect,
      onSelect: () => {
        handleSelect(chip.value);
      },
      orientation,
      selected: selectedChips.includes(chip.value),
      selectedChipItemClassName,
      shape,
      showIconOnlyWhenSelected,
      size,
      value: chip.value,
      variant,
      ...selectedChips.includes(chip.value) ? { ref: selectedChipItemRef } : {}
    },
    chip.value
  )) });
});

const DialogOverlay = () => /* @__PURE__ */ jsx(
  Overlay$1,
  {
    className: cn$1(
      "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    )
  }
);
DialogOverlay.displayName = "DialogOverlay";

const DialogContent = ({
  className,
  children,
  preventCloseOnOutsideInteraction
}) => {
  const handleOutsideInteraction = (e) => {
    if (preventCloseOnOutsideInteraction) {
      e.preventDefault();
    }
  };
  return /* @__PURE__ */ jsxs(Portal$1, { children: [
    /* @__PURE__ */ jsx(DialogOverlay, {}),
    /* @__PURE__ */ jsx(
      Content$2,
      {
        className: cn$1(
          "fixed ltr:left-[50%] rtl:right-[50%] top-[50%] z-50 flex flex-col justify-center text-center w-full sm:max-w-md max-w-sm rounded-xl ltr:translate-x-[-50%] rtl:translate-x-[50%] translate-y-[-50%] bg-white p-md shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
          className
        ),
        onInteractOutside: handleOutsideInteraction,
        children
      }
    )
  ] });
};
DialogContent.displayName = "DialogContent";

const DialogHeader = ({
  children,
  className
}) => /* @__PURE__ */ jsx("div", { className: cn$1("flex flex-col mb-xs text-center", className), children });
DialogHeader.displayName = "DialogHeader";

const DialogTitle = ({
  children,
  className
}) => /* @__PURE__ */ jsx(Title, { className: cn$1("text-xl font-bold", className), children });
DialogTitle.displayName = "DialogTitle";

const DialogFooter = ({
  children
}) => /* @__PURE__ */ jsx("div", { className: cn$1("flex justify-center mt-sm"), children });
DialogFooter.displayName = "DialogFooter";

const Dialog = ({
  children,
  className,
  headerClassName,
  disableClose = false,
  footer,
  open,
  preventCloseOnOutsideInteraction,
  title = "",
  titleClassName,
  onOpenChange
}) => {
  return /* @__PURE__ */ jsx(Root$2, { onOpenChange, open, children: /* @__PURE__ */ jsxs(
    DialogContent,
    {
      className,
      disableClose,
      preventCloseOnOutsideInteraction,
      children: [
        disableClose ? null : /* @__PURE__ */ jsx(
          Close,
          {
            className: "flex justify-end rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none",
            role: "button",
            children: /* @__PURE__ */ jsx(CrossIcon, {})
          }
        ),
        /* @__PURE__ */ jsx(DialogHeader, { className: headerClassName, children: /* @__PURE__ */ jsx(DialogTitle, { className: titleClassName, children: title }) }),
        children,
        footer ? /* @__PURE__ */ jsx(DialogFooter, { children: footer }) : null
      ]
    }
  ) });
};

const GroupingContainer = ({
  bodyClassName,
  children,
  className,
  header,
  headerClassName
}) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn$1(
        "border border-orange-400 rounded-xl text-md flex flex-col",
        className
      ),
      children: [
        header ? /* @__PURE__ */ jsx(
          "div",
          {
            className: cn$1(
              "bg-orange-400 inline-flex font-bold w-auto items-center rounded-br-lg rounded-tl-lg px-sm self-start",
              headerClassName
            ),
            children: header
          }
        ) : null,
        children ? /* @__PURE__ */ jsx("div", { className: cn$1("py-xs px-md", bodyClassName), children }) : null
      ]
    }
  );
};
GroupingContainer.displayName = "GroupingContainer";

const inlineNotificationVariants = cva(
  "flex w-full items-center justify-between gap-xs rounded-md border p-xs",
  {
    variants: {
      variant: {
        error: "border-red-200 bg-red-25 text-red-600",
        success: "border-green-200 bg-green-25 text-green-600",
        info: "border-blue-200 bg-blue-25 text-blue-600",
        warning: "border-orange-200 bg-orange-25 text-orange-600"
      },
      align: {
        center: "text-center",
        left: "text-left",
        right: "text-right"
      }
    },
    defaultVariants: {
      variant: "error",
      align: "left"
    }
  }
);
const InlineNotification = ({
  icon,
  heading,
  children = /* @__PURE__ */ jsx(Fragment, { children: " " }),
  actionButton,
  className,
  headingClassName,
  variant = "error",
  styles,
  testId = "inline-notification",
  size = "xs"
}) => {
  const headingId = `${testId}-heading`;
  const descriptionId = `${testId}-description`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "aria-describedby": descriptionId,
      "aria-labelledby": headingId,
      className: cn$1(inlineNotificationVariants({ variant }), className),
      "data-testid": `${testId}-container`,
      role: "alert",
      ...styles && { style: styles },
      children: [
        icon ? /* @__PURE__ */ jsx("div", { className: "m-xs", "data-testid": `${testId}-icon`, children: icon }) : null,
        /* @__PURE__ */ jsxs("div", { className: "w-full", "data-testid": `${testId}-content`, children: [
          heading ? /* @__PURE__ */ jsx(
            Typography,
            {
              className: headingClassName,
              testId: headingId,
              variant: size,
              weight: "bold",
              children: heading
            }
          ) : null,
          /* @__PURE__ */ jsx("div", { "data-testid": descriptionId, children })
        ] }),
        actionButton !== void 0 && actionButton
      ]
    }
  );
};

const OOSCount = ({ count }) => /* @__PURE__ */ jsx("div", { className: "flex size-2xl items-center justify-center rounded-lg bg-white", children: /* @__PURE__ */ jsxs(Typography, { className: "text-gray-500", variant: "sm", weight: "bold", children: [
  "+",
  count
] }) });

const OOSItemsGrid = ({
  className,
  list = [],
  outOfStockLabel = "Out of Stock"
}) => {
  const isShowOutOfStockCount = list.length > 4;
  const outOfStockCount = list.length - 3;
  const visibleOOSItems = list.slice(0, isShowOutOfStockCount ? 3 : 4);
  const isSingleItem = visibleOOSItems.length === 1;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn$1(
        "flex size-[108px] rounded-lg border-[0.5px] border-solid border-madison-50 relative  items-center justify-center p-2xs",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn$1(
              "grid size-full gap-xs p-[6px] rounded-lg bg-gray-50 items-center",
              {
                "grid-cols-2": !isSingleItem
              }
            ),
            children: [
              visibleOOSItems.length === 1 ? /* @__PURE__ */ jsx(
                ProductImage,
                {
                  imageUrl: visibleOOSItems[0].product.images[0].url,
                  isGrayScale: true
                },
                visibleOOSItems[0].product.code
              ) : visibleOOSItems.map((item) => /* @__PURE__ */ jsx(
                ProductImage,
                {
                  className: cn$1({
                    "size-2xl": !isSingleItem
                  }),
                  imageUrl: item.product.images[0].url,
                  isGrayScale: true
                },
                item.product.code
              )),
              isShowOutOfStockCount ? /* @__PURE__ */ jsx(OOSCount, { count: outOfStockCount }) : null
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          Badge,
          {
            className: "absolute inset-x-[6px] top-[41px] flex items-center justify-center",
            color: "red",
            mode: "light",
            shape: "pill",
            size: "tiny",
            children: outOfStockLabel
          }
        )
      ]
    }
  );
};

const PasswordInput = forwardRef(
  ({
    className,
    defaultValue,
    disabled = false,
    error,
    helperText,
    icon,
    iconPosition = "right",
    id = "floating_input",
    label,
    name,
    onBlur,
    onChange,
    testId = "text-input"
  }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const toggleShowPassword = () => {
      setShowPassword(!showPassword);
    };
    return /* @__PURE__ */ jsx(
      TextField,
      {
        className,
        defaultValue,
        disabled,
        error,
        helperText,
        icon: icon || /* @__PURE__ */ jsx(
          PasswordEyeIcons,
          {
            showPassword,
            toggleShowPassword
          }
        ),
        iconPosition,
        id,
        label,
        name,
        onBlur,
        onChange,
        ref,
        testId,
        type: showPassword ? "text" : "password"
      }
    );
  }
);
const PasswordEyeIcons = ({
  showPassword,
  toggleShowPassword
}) => {
  return /* @__PURE__ */ jsx(Button, { onClick: toggleShowPassword, variant: "ghost", children: showPassword ? /* @__PURE__ */ jsx(EyeIcon, { variant: "thin" }) : /* @__PURE__ */ jsx(EyeSlashIcon, { variant: "thin" }) });
};
PasswordInput.displayName = "PasswordInput";

const useClickOutside = (ref, callback) => {
  const handleClickOutside = useCallback(
    (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    },
    [ref, callback]
  );
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);
};

const borderRadius = {
  "2xs": "0.0625rem",
  // 1px
  xs: "0.125rem",
  // 2px
  sm: "0.25rem",
  // 4px
  md: "0.375rem",
  // 6px
  lg: "0.5rem",
  // 8px
  xl: "0.75rem",
  // 12px
  "2xl": "1rem",
  // 16px
  "3xl": "1.5rem"
  // 24px
};

const borderWidth = {
  "2xs": "0.03125rem",
  // 0.5px
  xs: "0.0625rem",
  // 1px
  sm: "0.125rem",
  // 2px
  md: "0.1875rem",
  // 3px
  lg: "0.25rem",
  // 4px
  xl: "0.375rem",
  // 6px
  "2xl": "0.5rem",
  // 8px
  "3xl": "0.75rem"
  // 12px
};

const colors = {
  // main colors
  primary: "#0E5AA7",
  "primary-active": "#0B4C8E",
  "primary-hover": "#093A6C",
  secondary: "rgba(14, 90, 167, 0.05)",
  "secondary-active": "rgba(14, 90, 167, 0.10)",
  "secondary-hover": "rgba(14, 90, 167, 0.20)",
  transparent: "transparent",
  black: "#000000",
  white: "#FFFFFF",
  // c4-blue color palette
  "c4blue-25": "#F4FBFE",
  "c4blue-50": "#C2D5E8",
  "c4blue-100": "#92B4D7",
  "c4blue-200": "#6293C5",
  "c4blue-300": "#3172B4",
  "c4blue-400": "#0E5AA7",
  "c4blue-500": "#0B4C8E",
  "c4blue-600": "#093A6C",
  "c4blue-700": "#06284B",
  "c4blue-800": "#031629",
  // c4-red color palette
  "c4red-25": "#FEF1F1",
  "c4red-50": "#FAC8C8",
  "c4red-100": "#F79C9D",
  "c4red-200": "#F37172",
  "c4red-300": "#F04547",
  "c4red-400": "#DD2326",
  "c4red-500": "#BC1E20",
  "c4red-600": "#9A1819",
  "c4red-700": "#6B1011",
  "c4red-800": "#3B0909",
  // other brand colors
  mafgold: "#BD9E56",
  sharemaroon: "#8A1538",
  sharegold: "#B4975A",
  sharebrown: "#2F2707",
  shareprimary: "#B5064D",
  shareblue: "#0E5AA70D",
  sharegray: "#D9D9D9",
  sharegraywhite: "F7F8F9",
  sharedarkblue: "#1877F2",
  shareblack: "#000408",
  nightblack: "#1A1A1A",
  sharedeepblue: "#001478",
  "profile-blue": "#1271D2",
  "profile-gray": "#F7F8F9",
  "whatsapp-green": "#25D366",
  // gold color palette
  "gold-25": "#FDF8F2",
  "gold-50": "#F7E0C2",
  "gold-100": "#F2C892",
  "gold-200": "#ECB062",
  "gold-300": "#E79832",
  "gold-400": "#E3870F",
  "gold-500": "#C1720C",
  "gold-600": "#935709",
  "gold-700": "#663C06",
  "gold-800": "#382103",
  // yellow color palette
  "yellow-25": "#FFFDF4",
  "yellow-50": "#FFF6C9",
  "yellow-100": "#FFF09E",
  "yellow-200": "#FFEA73",
  "yellow-300": "#FFE348",
  "yellow-400": "#FFDF28",
  "yellow-500": "#D9BD22",
  "yellow-600": "#A6911A",
  "yellow-700": "#736412",
  "yellow-800": "#40370A",
  // persia color palette
  "persia-25": "#E6F4F2",
  "persia-50": "#BFE4E1",
  "persia-100": "#8CCFC9",
  "persia-200": "#59BAB1",
  "persia-300": "#26A599",
  "persia-400": "#009688",
  "persia-500": "#007F73",
  "persia-600": "#006158",
  "persia-700": "#00433D",
  "persia-800": "#002522",
  // madison color palette
  "madison-25": "#F0F3F7",
  "madison-50": "#C1CFDE",
  "madison-100": "#90AAC3",
  "madison-200": "#5F84A9",
  "madison-300": "#2E5F8F",
  "madison-400": "#0A437C",
  "madison-500": "#083969",
  "madison-600": "#062B50",
  "madison-700": "#041E37",
  "madison-800": "#051423",
  // polo color palette
  "polo-25": "#F2F5F8",
  "polo-50": "#DFE8F2",
  "polo-100": "#C5D6E8",
  "polo-200": "#ACC5DE",
  "polo-300": "#92B3D4",
  "polo-400": "#80A6CD",
  "polo-500": "#6C8DAE",
  "polo-600": "#536C85",
  "polo-700": "#394A5C",
  "polo-800": "#202933",
  // minsk color palette
  "minsk-25": "#F5F5F9",
  "minsk-50": "#CDCDE5",
  "minsk-100": "#A6A6D0",
  "minsk-200": "#7F7FBB",
  "minsk-300": "#5858A7",
  "minsk-400": "#3B3B98",
  "minsk-500": "#323281",
  "minsk-600": "#262662",
  "minsk-700": "#1A1A44",
  "minsk-800": "#0E0E26",
  // purple color palette
  "purple-25": "#F6F1F5",
  "purple-50": "#DAC6D7",
  "purple-100": "#BE98B7",
  "purple-200": "#A16B97",
  "purple-300": "#843D77",
  "purple-400": "#6F1C60",
  "purple-500": "#5E1751",
  "purple-600": "#48123E",
  "purple-700": "#320C2B",
  "purple-800": "#1B0718",
  // gray color palette
  "gray-15": "#FAFAFB",
  "gray-25": "#F4F4F4",
  "gray-50": "#EAEAEA",
  "gray-100": "#DCDCDC",
  "gray-200": "#C3C3C3",
  "gray-300": "#B2B2B2",
  "gray-400": "#979797",
  "gray-500": "#747474",
  "gray-600": "#505050",
  "gray-700": "#1F1F1F",
  "gray-800": "#141414",
  // red color palette
  "red-25": "#FDF4F4",
  "red-50": "#FBCFCF",
  "red-100": "#F9A8A9",
  "red-200": "#F78283",
  "red-300": "#F45C5D",
  "red-400": "#F34041",
  "red-500": "#CE3637",
  "red-600": "#9E292A",
  "red-700": "#6D1C1D",
  "red-800": "#3C1010",
  // orange color palette
  "orange-15": "#F1B91A",
  "orange-25": "#FEFCF5",
  "orange-50": "#FCEECF",
  "orange-100": "#FAE0A9",
  "orange-200": "#F7D283",
  "orange-300": "#F5C45D",
  "orange-400": "#F3BA40",
  "orange-500": "#CF9E36",
  "orange-600": "#9E792A",
  "orange-700": "#6D541D",
  "orange-800": "#3D2F10",
  // green color palette
  "green-25": "#E9F6F1",
  "green-50": "#CEEEDB",
  "green-100": "#A6E0BF",
  "green-200": "#7FD2A3",
  "green-300": "#58C486",
  "green-400": "#3ABA71",
  "green-500": "#319E60",
  "green-600": "#267949",
  "green-700": "#1A5433",
  "green-800": "#0F2F1C",
  // blue color palette
  "blue-25": "#F1FAFE",
  "blue-50": "#C7EBF9",
  "blue-100": "#9BDBF4",
  "blue-200": "#6ECCEF",
  "blue-300": "#41BCEA",
  "blue-400": "#20B0E6",
  "blue-500": "#1B96C3",
  "blue-600": "#157296",
  "blue-700": "#0E4F68",
  "blue-800": "#082C39"
};

const font = {
  en: "Montserrat",
  ar: "Cairo"
};

const fontSize = {
  "2xs": "0.5rem",
  // 8px
  xs: "0.625rem",
  // 10px
  sm: "0.75rem",
  // 12px
  md: "0.875rem",
  // 14px
  lg: "1rem",
  // 16px
  xl: "1.25rem",
  // 20px
  "2xl": "1.5rem",
  // 24px
  "3xl": "1.75rem",
  // 28px
  "4xl": "2rem"
  // 32px
};

const fontWeight = {
  regular: "400",
  medium: "500",
  semiBold: "600",
  bold: "700"
};

const spacing = {
  "4xs": "0.0625rem",
  // 1px
  "3xs": "0.125rem",
  // 2px
  "2xs": "0.25rem",
  // 4px
  "1.5xs": "0.375rem",
  // 6px
  xs: "0.5rem",
  // 8px
  sm: "0.75rem",
  // 12px
  md: "1rem",
  // 16px
  lg: "1.5rem",
  // 24px
  xl: "2rem",
  // 32px
  "2xl": "2.5rem",
  // 40px
  "3xl": "3rem",
  // 48px
  "4xl": "3.5rem"
  // 56px
};

const gap = {
  ...spacing
};

const margin = {
  ...spacing
};

const padding = {
  ...spacing
};

const breakpoints = {
  xs: "576px",
  // X-Small devices
  sm: "768px",
  // Small devices
  md: "992px",
  // medium devices
  lg: "1200px"
  // large devices
};

const theme = {
  colors,
  fontFamily: {
    montserrat: [font.en, "sans-serif"],
    cario: [font.ar, "serif"]
  },
  fontWeight,
  fontSize,
  container: {
    center: true,
    padding: 0,
    screens: {
      lg: "1232px"
    }
  },
  screens: breakpoints,
  extend: {
    spacing,
    borderRadius,
    borderWidth,
    margin,
    padding,
    gap,
    transitionProperty: {
      height: "height"
    },
    boxShadow: {
      "sticky-footer": "0 -1px 4px 0px rgba(0, 0, 0, 0.2)",
      "profile-popover": "0px 0px 5px -2px rgba(0,0,0,0.75)"
    },
    keyframes: {
      "accordion-down": {
        from: { height: "0" },
        to: { height: "var(--radix-accordion-content-height)" }
      },
      "accordion-up": {
        from: { height: "var(--radix-accordion-content-height)" },
        to: { height: "0" }
      },
      progress: {
        "0%": { transform: "translateX(-100%)" },
        "100%": { transform: "translateX(100%)" }
      },
      "fade-in-up": {
        "0%": {
          opacity: "0",
          transform: "translateY(-40px)"
        },
        "100%": {
          opacity: "1",
          transform: "translateY(0)"
        }
      },
      marquee: {
        "0%": { transform: "translateX(0%)" },
        "100%": { transform: "translateX(-100%)" }
      },
      "move-strips": {
        "0%": { backgroundPosition: "100px 0" },
        "100%": { backgroundPosition: "0 0" }
      },
      ripple: {
        "0%": { transform: "scale(1.5)", opacity: "0.6" },
        "100%": { transform: "scale(3)", opacity: "0" }
      }
    },
    animation: {
      "accordion-down": "accordion-down 0.2s ease-out",
      "accordion-up": "accordion-up 0.2s ease-out",
      "progress-linear": "progress 1.5s infinite linear",
      "fade-in-up": "fade-in-up 0.2s ease-in-out",
      marquee: "marquee 12s linear infinite",
      ripple: "ripple 1s ease-out infinite",
      strips: "move-strips 5s infinite linear"
    },
    backgroundImage: {
      "striped-gradient": "linear-gradient(45deg, #0E5AA7 25%, #6293C5 25%, #6293C5 50%, #0E5AA7 50%, #0E5AA7 75%, #6293C5 75%, #6293C5)"
    },
    backgroundColor: {
      "skeleton-loader": "#eaeaea"
    }
  }
};

const config = {
  theme,
  plugins: []
};

const numericBreakpoints = Object.values(
  config.theme?.screens || {}
).map((value) => Number(value.replace("px", "")));
const getScreenSize = (width) => {
  if (width < numericBreakpoints[1]) {
    return "mobile" /* Mobile */;
  } else if (width < numericBreakpoints[2]) {
    return "tablet" /* Tablet */;
  } else if (width < numericBreakpoints[3]) {
    return "tabletLarge" /* TabletLarge */;
  }
  return "desktop" /* Desktop */;
};
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState(
    isClient() ? getScreenSize(window.innerWidth) : "desktop" /* Desktop */
  );
  useEffect(() => {
    if (!isClient()) {
      return;
    }
    const handleResize = () => {
      setScreenSize(getScreenSize(window.innerWidth));
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return {
    isMobile: screenSize === "mobile" /* Mobile */,
    isTablet: screenSize === "tablet" /* Tablet */,
    isLargeTablet: screenSize === "tabletLarge" /* TabletLarge */,
    isDesktop: screenSize === "desktop" /* Desktop */
  };
};

const PopOver = ({
  align = "center",
  animationType,
  children,
  defaultOpen = false,
  triggerOnHover = false,
  shouldCloseOnOutsideClick = true,
  showTip = true,
  side = "bottom",
  triggeredComponent
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(defaultOpen);
  const containerRef = useRef(null);
  const handlePopoverStateChange = (isOpen) => {
    setIsPopoverOpen(isOpen);
  };
  const handleOutsideClickClose = () => {
    if (shouldCloseOnOutsideClick && !triggerOnHover) {
      setIsPopoverOpen(false);
    }
  };
  const handleMouseEnter = () => {
    setIsPopoverOpen(true);
  };
  const handleMouseLeave = () => {
    setIsPopoverOpen(false);
  };
  useClickOutside(containerRef, handleOutsideClickClose);
  return /* @__PURE__ */ jsx("div", { ref: containerRef, children: /* @__PURE__ */ jsxs(Root$3, { onOpenChange: handlePopoverStateChange, open: isPopoverOpen, children: [
    /* @__PURE__ */ jsx(
      Trigger$2,
      {
        className: "outline-none",
        ...triggerOnHover ? {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave
        } : {},
        children: triggeredComponent
      }
    ),
    /* @__PURE__ */ jsxs(
      Content$3,
      {
        align,
        className: cn$1("outline-none", {
          "animate-fade-in-up rounded-lg": animationType === "fade-in-up"
        }),
        ...triggerOnHover ? {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave
        } : {},
        side,
        children: [
          children,
          showTip ? /* @__PURE__ */ jsx(
            Arrow,
            {
              className: "fill-current text-gray-200",
              "data-testid": "popover-arrow",
              height: 15,
              width: 30
            }
          ) : null
        ]
      }
    )
  ] }) });
};

const RadioInput = ({
  disabled = false,
  isChecked,
  value,
  onValueChange,
  name,
  isLoading = false
}) => {
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn$1("flex relative rounded-full p-2xs", {
        "hover:bg-gray-50 cursor-pointer": !disabled
      }),
      children: [
        isLoading ? /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("div", { className: "absolute -z-10 size-[20px] animate-ripple rounded-full bg-primary" }),
          /* @__PURE__ */ jsx(Overlay, {})
        ] }) : null,
        /* @__PURE__ */ jsx(
          "input",
          {
            checked: isChecked,
            className: cn$1(
              "peer appearance-none rounded-full border-gray-300 border-2 size-[20px] relative",
              {
                "border-primary": isChecked,
                "border-gray-300 opacity-40 cursor-not-allowed": disabled,
                "cursor-pointer": !disabled
              }
            ),
            disabled,
            id: value,
            name,
            onChange: () => {
              onValueChange(value);
            },
            type: "radio",
            value
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            className: cn$1(
              "size-sm rounded-full pointer-events-none absolute left-1/2 top-1/2 -translate-y-1/2 opacity-0 transition-opacity peer-checked:opacity-100 ltr:-translate-x-1/2 rtl:translate-x-1/2",
              disabled && isChecked ? "bg-gray-300 opacity-40" : "bg-primary"
            )
          }
        )
      ]
    }
  );
};

const RadioLabel = ({
  label,
  disabled,
  labelClassname,
  labelVariant,
  labelWeight
}) => /* @__PURE__ */ jsx(
  Typography,
  {
    className: cn$1(
      {
        "opacity-40": disabled
      },
      labelClassname
    ),
    variant: labelVariant,
    weight: labelWeight,
    children: label
  }
);

const Radio = forwardRef(function Radio2({
  className,
  disabled = false,
  isChecked,
  label,
  value,
  alignLabel = "right",
  labelVariant,
  labelWeight,
  labelClassname,
  name,
  isLoading = false,
  onValueChange
}, selectedRadioRef) {
  const RadioLabelComponent = /* @__PURE__ */ jsx(
    RadioLabel,
    {
      label,
      labelClassname,
      labelVariant,
      labelWeight
    }
  );
  return /* @__PURE__ */ jsxs(
    "label",
    {
      className: cn$1("flex items-center gap-xs cursor-pointer", className),
      htmlFor: value,
      ref: selectedRadioRef,
      children: [
        alignLabel === "left" && RadioLabelComponent,
        /* @__PURE__ */ jsx(
          RadioInput,
          {
            disabled,
            isChecked,
            isLoading,
            name,
            onValueChange,
            value
          }
        ),
        alignLabel === "right" && RadioLabelComponent
      ]
    }
  );
});

const RadioGroup = ({
  className,
  disabled,
  options,
  selectedValue,
  onValueChange
}) => {
  return /* @__PURE__ */ jsx("div", { className, children: options.map((option) => /* @__PURE__ */ jsx(
    Radio,
    {
      className: option.className,
      disabled: disabled || option.disabled,
      isChecked: selectedValue === option.value,
      label: option.label,
      onValueChange,
      value: option.value
    },
    option.value
  )) });
};

const sheetVariants = cva(
  "fixed z-50 max-h-[700px] flex-col overflow-x-hidden overflow-y-scroll bg-white px-0 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out md:max-h-screen",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 flex rounded-b-2xl data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom: "inset-x-0 bottom-0 flex rounded-t-2xl data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 flex h-full w-[411px] rounded-r-2xl ltr:data-[state=closed]:slide-out-to-left ltr:data-[state=open]:slide-in-from-left rtl:data-[state=closed]:slide-out-to-right rtl:data-[state=open]:slide-in-from-right",
        right: "inset-y-0 right-0 flex h-full w-[411px] rounded-l-2xl ltr:data-[state=closed]:slide-out-to-right ltr:data-[state=open]:slide-in-from-right rtl:data-[state=closed]:slide-out-to-left rtl:data-[state=open]:slide-in-from-left"
      }
    },
    defaultVariants: {
      side: "right"
    }
  }
);

const SheetOverlay = () => /* @__PURE__ */ jsx(Overlay$1, { className: "fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" });
SheetOverlay.displayName = "SheetOverlay";

const SheetContent = forwardRef(
  ({
    children,
    className,
    id,
    preventCloseOnOutsideInteraction,
    side = "right",
    ...props
  }, ref) => {
    const handleOutsideInteraction = (e) => {
      if (preventCloseOnOutsideInteraction) {
        e.preventDefault();
      }
    };
    return /* @__PURE__ */ jsx(Portal$1, { children: /* @__PURE__ */ jsxs("div", { className: "c4c-web", id, children: [
      /* @__PURE__ */ jsx(SheetOverlay, {}),
      /* @__PURE__ */ jsx(
        Content$2,
        {
          className: cn$1(sheetVariants({ side, className })),
          onInteractOutside: handleOutsideInteraction,
          ref,
          ...props,
          children
        }
      )
    ] }) });
  }
);
SheetContent.displayName = "SheetContent";

const SheetHeader = ({
  children,
  isTitleBorder = true
}) => /* @__PURE__ */ jsxs("div", { className: "sticky top-0 flex flex-col gap-2xs bg-white px-md pt-sm text-left md:pt-md", children: [
  children,
  isTitleBorder ? /* @__PURE__ */ jsx("div", { className: "-mx-md mt-xs border-b-xs border-b-gray-50 md:mt-md" }) : null
] });
SheetHeader.displayName = "SheetHeader";

const SheetTitle = ({
  children,
  hideCrossIcon,
  isFloatingCrossIcon
}) => /* @__PURE__ */ jsxs(Title, { className: "flex items-center justify-between text-2xl font-bold", children: [
  children,
  !hideCrossIcon ? /* @__PURE__ */ jsx(
    Close,
    {
      className: cn$1(
        "md:top-lg rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2",
        {
          "absolute top-[-16px] right-2 p-2xs bg-white opacity-100 rounded-full": isFloatingCrossIcon
        }
      ),
      role: "button",
      children: /* @__PURE__ */ jsx(CrossIcon, { size: "md" })
    }
  ) : null
] });
SheetTitle.displayName = "SheetTitle";

const SheetDescription = ({
  children
}) => /* @__PURE__ */ jsx(Description, { className: "text-md", children });
SheetDescription.displayName = "SheetDescription";

const SheetFooter = ({
  children
}) => /* @__PURE__ */ jsx("div", { className: "p-md", children });
SheetFooter.displayName = "SheetFooter";

const Sheet = e.forwardRef(
  ({
    children,
    className,
    footer,
    hideCrossIcon,
    hideScrollBar = false,
    id,
    isTitleBorder = true,
    isFloatingCrossIcon,
    open,
    preventCloseOnOutsideInteraction = false,
    subtitle = "",
    title = "",
    variant = "right",
    onOpenChange
  }, ref) => {
    const screenSize = useScreenSize();
    const side = screenSize.isDesktop ? variant : "bottom";
    return /* @__PURE__ */ jsx(Root$2, { onOpenChange, open, children: /* @__PURE__ */ jsxs(
      SheetContent,
      {
        className,
        id,
        preventCloseOnOutsideInteraction,
        ref,
        role: "dialog",
        side,
        ...!subtitle ? { "aria-describedby": void 0 } : {},
        children: [
          /* @__PURE__ */ jsxs(SheetHeader, { isTitleBorder, children: [
            /* @__PURE__ */ jsx(
              SheetTitle,
              {
                hideCrossIcon,
                isFloatingCrossIcon,
                children: title
              }
            ),
            subtitle ? /* @__PURE__ */ jsx(SheetDescription, { children: subtitle }) : null
          ] }),
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn$1(
                "flex w-full grow flex-col gap-md px-md overflow-scroll pt-sm md:pt-md",
                {
                  "no-scrollbar overflow-y-auto": hideScrollBar
                }
              ),
              children
            }
          ),
          footer ? /* @__PURE__ */ jsx(SheetFooter, { children: footer }) : null
        ]
      }
    ) });
  }
);
Sheet.displayName = "Sheet";

const ResponsiveModal = ({
  children,
  dialogClassName,
  disableDialogClose,
  footer,
  dialogHeaderClassName,
  hideSheetCrossIcon,
  hideSheetScrollBar,
  isSheetFloatingCrossIcon,
  isSheetTitleBorder,
  onOpenChange,
  open,
  preventCloseOnOutsideInteraction,
  sheetClassName,
  sheetSubtitle,
  title,
  dialogTitleClassName
}) => {
  const { isMobile } = useScreenSize();
  return isMobile ? /* @__PURE__ */ jsx(
    Sheet,
    {
      className: sheetClassName,
      footer,
      hideCrossIcon: hideSheetCrossIcon,
      hideScrollBar: hideSheetScrollBar,
      isFloatingCrossIcon: isSheetFloatingCrossIcon,
      isTitleBorder: isSheetTitleBorder,
      onOpenChange,
      open,
      preventCloseOnOutsideInteraction,
      subtitle: sheetSubtitle,
      title,
      variant: "bottom",
      children
    }
  ) : /* @__PURE__ */ jsx(
    Dialog,
    {
      className: dialogClassName,
      disableClose: disableDialogClose,
      footer,
      headerClassName: dialogHeaderClassName,
      onOpenChange,
      open,
      preventCloseOnOutsideInteraction,
      title,
      titleClassName: dialogTitleClassName,
      children
    }
  );
};

const searchBarVariants = cva(
  "h-2xl w-full rounded-full border border-gray-300 bg-white px-2xl py-[10px] outline-none focus:ring-2",
  {
    variants: {
      variant: {
        header: "text-black md:pl-lg md:pr-4xl",
        default: "pl-3xl",
        address: "h-[44px] rounded-sm border-gray-100 py-sm pl-md pr-2xl placeholder-gray-500"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
const searchButtonVariants = cva(
  "absolute top-1/2 flex -translate-y-1/2 items-center",
  {
    variants: {
      variant: {
        header: "left-sm md:left-auto md:right-sm",
        default: "left-md",
        address: "right-sm rounded-sm"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
const crossButtonVariants = cva(
  "absolute right-md top-1/2 flex -translate-y-1/2 items-center",
  {
    variants: {
      variant: {
        header: "right-sm mr-xs md:right-2xl",
        default: "",
        address: "right-sm"
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

const SearchBar = forwardRef(
  ({
    autoFocusEnabled = false,
    className,
    clearButtonLabel,
    placeholder,
    variant = "default",
    value,
    id = "search-bar",
    isReadOnly = false,
    onChange,
    onClick,
    onBlurHandler,
    onFocusHandler,
    onKeyDown,
    onSubmit
  }, forwardedRef) => {
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef(null);
    const handleInputChange = (event) => {
      const { value: input = "" } = event.target;
      const sanitizedValue = getSanitizedValue(input);
      setInputValue(sanitizedValue);
      onChange?.(sanitizedValue);
    };
    const clearInput = (event) => {
      event.stopPropagation();
      setInputValue("");
      inputRef.current?.focus();
      onChange?.("");
    };
    const onFocus = () => {
      if (onFocusHandler) onFocusHandler();
    };
    const onSubmitHandler = (e) => {
      e.preventDefault();
      if (inputValue) {
        onSubmit?.(inputValue, true);
      }
    };
    useEffect(() => {
      setInputValue(value ?? "");
    }, [value]);
    useEffect(() => {
      if (autoFocusEnabled) {
        inputRef.current?.focus();
      }
    }, [autoFocusEnabled]);
    return /* @__PURE__ */ jsxs("form", { className: "relative", onSubmit: onSubmitHandler, children: [
      /* @__PURE__ */ jsx(
        "input",
        {
          autoComplete: "off",
          className: cn$1(searchBarVariants({ variant, className }), {
            "cursor-pointer": isReadOnly
          }),
          id,
          onBlur: onBlurHandler,
          onChange: handleInputChange,
          onClick,
          onFocus,
          onKeyDown,
          placeholder,
          readOnly: isReadOnly,
          ref: (node) => {
            inputRef.current = node;
            if (typeof forwardedRef === "function") {
              forwardedRef(node);
            } else if (forwardedRef) {
              forwardedRef.current = node;
            }
          },
          type: "text",
          value: inputValue
        }
      ),
      inputValue ? /* @__PURE__ */ jsx(
        Button,
        {
          "aria-label": clearButtonLabel,
          className: cn$1(crossButtonVariants({ variant })),
          "data-testid": "clear-button",
          onClick: clearInput,
          type: "button",
          variant: "ghost",
          children: /* @__PURE__ */ jsx(
            FilledCrossIcon,
            {
              color: variant === "default" ? "gray-200" : "gray-500",
              size: "md",
              variant: "filled"
            }
          )
        }
      ) : null,
      /* @__PURE__ */ jsx(
        Button,
        {
          className: cn$1(searchButtonVariants({ variant }), {
            hidden: variant === "address" && inputValue
          }),
          type: "submit",
          variant: "ghost",
          children: /* @__PURE__ */ jsx(MagnifyingGlass, { color: "gray-500", size: "md" })
        }
      )
    ] });
  }
);
SearchBar.displayName = "SearchBar";

const getToastNotificationIcon = (variant) => {
  const icons = {
    success: /* @__PURE__ */ jsx(CircleCheckIcon, { color: "white" }),
    error: /* @__PURE__ */ jsx(WarningOctagonIcon, { color: "white" }),
    info: /* @__PURE__ */ jsx(InfoIcon, { color: "white" }),
    warning: /* @__PURE__ */ jsx(WarningIcon, { color: "white" })
  };
  return icons[variant];
};

const toastNotificationVariants = cva(
  "flex w-full items-center justify-between gap-md rounded-sm px-md py-sm text-white",
  {
    variants: {
      variant: {
        error: "bg-red-500",
        success: "bg-green-600",
        info: "bg-blue-600",
        warning: "bg-gold-400"
      }
    },
    defaultVariants: {
      variant: "info"
    }
  }
);

const ToastNotification = ({
  onActionClick,
  actionLabel,
  message,
  variant = "error"
}) => {
  return /* @__PURE__ */ jsxs("div", { className: cn$1(toastNotificationVariants({ variant })), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-md", children: [
      getToastNotificationIcon(variant),
      /* @__PURE__ */ jsx(Typography, { children: message })
    ] }),
    actionLabel ? /* @__PURE__ */ jsx(
      Button,
      {
        className: "font-bold text-white",
        onClick: onActionClick,
        variant: "ghost",
        children: actionLabel
      }
    ) : null
  ] });
};

const notchMessageCardVariants = cva(
  `relative gap-sm rounded-lg rounded-bl-none border p-sm shadow-md
   before:absolute before:bottom-[-15px] before:-translate-x-px before:-translate-y-px before:border-b-[13px] before:border-l-[21px] 
   before:border-solid before:border-transparent after:absolute after:left-0
   after:top-full after:border-b-[12px] after:border-l-[20px] after:border-solid after:border-transparent ltr:before:left-0 rtl:before:right-[-2px]`,
  {
    variants: {
      variant: {
        light: `border-blue-100 bg-blue-25 before:border-l-blue-100 after:border-l-blue-25`,
        dark: `border-blue-25 bg-blue-100 before:border-l-blue-25 after:border-l-blue-100`
      }
    },
    defaultVariants: {
      variant: "light"
    }
  }
);

const NotchMessageCard = ({
  title,
  description,
  variant = "light"
}) => {
  return /* @__PURE__ */ jsxs("div", { className: cn$1(notchMessageCardVariants({ variant })), children: [
    /* @__PURE__ */ jsx(Typography, { variant: "md", weight: "bold", children: title }),
    /* @__PURE__ */ jsx(Typography, { variant: "md", children: description })
  ] });
};

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge, Bullet, Button, Card, Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, CarouselScroll, Checkbox, Chip, ClockBadge, Dialog, FormFieldHelperText, FormatTextToJSX, GroupingContainer, InlineNotification, LineSeparator, LinearProgressBar, Marquee, NotchMessageCard, OOSItemsGrid, Overlay, PasswordInput, PopOver, ProductImage, ProgressBar, Radio, RadioGroup, RangeSlider, ResponsiveModal, ScrollToTop, SearchBar, Select, Sheet, Skeleton, Spinner, TextField, TextInput, ToastNotification, Toaster, Toggle, Typography, inlineNotificationVariants, Jt as toast, useCarousel };
//# sourceMappingURL=index.mjs.map
